import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, ChevronRight, ChevronDown, Pencil, Trash2, FolderTree, Package, Warehouse, Settings, Building2, Check, X, Users, Shield, Key, Lock, Unlock, RefreshCw, Eye, EyeOff, Search, Bot, Copy, AlertTriangle, Download, Save, CalendarDays, Target, Network, Ruler, Tags, Boxes, Plug, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { COUNTRIES_SORTED } from '@/lib/countries';
import { MODULE_CATALOG, DEFAULT_ENABLED_MODULES } from '@/lib/modules';
import WhatsAppAyarKarti from '@/components/WhatsAppAyarKarti';

/**
 * Admin panelinin 13 bölümü tek sıraya sığmıyordu: alt alta sarıyor, hangisinin
 * seçili olduğu kaybolup hepsi tek bir kalabalık gibi görünüyordu (kullanıcı,
 * 2026-09-18). Artık GRUPLU bir yan menü — bölümler dört başlık altında ve
 * seçili olan dolgulu. Dar ekranda menü açılır listeye düşüyor.
 *
 * Bölüm adresin sonunda taşınıyor (`/erp/admin#departmanlar`): başka ekranlardan
 * doğrudan bağlantı verilebilsin ve sayfa yenilenince aynı bölüm açılsın diye.
 */
const BOLUMLER = [
  { grup: 'Erişim', kalemler: [
    { deger: 'users', ad: 'Kullanıcılar', ikon: Users },
    { deger: 'roles', ad: 'Rol Şablonları', ikon: Shield },
    { deger: 'mcp', ad: 'Yapay Zekâ Bağlantısı', ikon: Bot },
  ] },
  { grup: 'Organizasyon', kalemler: [
    { deger: 'companies', ad: 'Şirketler', ikon: Building2 },
    { deger: 'departmanlar', ad: 'Departman ve Pozisyonlar', ikon: Network },
    { deger: 'merkezler', ad: 'Sorumluluk Merkezleri', ikon: Target },
    { deger: 'warehouses', ad: 'Depolar', ikon: Warehouse },
  ] },
  { grup: 'Tanımlar', kalemler: [
    { deger: 'product-cats', ad: 'Ürün Kategorileri', ikon: Package },
    { deger: 'expense-cats', ad: 'Gider Kategorileri', ikon: Tags },
    { deger: 'fixed-asset-types', ad: 'Demirbaş Türleri', ikon: Boxes },
    { deger: 'units', ad: 'Birimler', ikon: Ruler },
  ] },
  { grup: 'Ayarlar', kalemler: [
    { deger: 'payroll', ad: 'Puantaj Ayarları', ikon: CalendarDays },
    { deger: 'integrations', ad: 'Entegrasyon Ayarları', ikon: Plug },
  ] },
];
const TUM_BOLUMLER = BOLUMLER.flatMap(g => g.kalemler);

export default function AdminPanel() {
  const [bolum, setBolum] = useState(() => {
    const capa = window.location.hash.replace('#', '');
    return TUM_BOLUMLER.some(k => k.deger === capa) ? capa : 'users';
  });
  const secili = TUM_BOLUMLER.find(k => k.deger === bolum);

  const bolumSec = (v) => {
    setBolum(v);
    // replaceState: her sekme tıklaması geri tuşuna bir adım eklemesin
    window.history.replaceState(null, '', `${window.location.pathname}#${v}`);
  };

  return (
    <div data-testid="admin-panel">
      <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight mb-1">Admin Paneli</h1>
      <p className="text-sm text-[#8a7e70] mb-5">{secili?.ad}</p>
      <Tabs value={bolum} onValueChange={bolumSec} orientation="vertical" className="w-full">
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          {/* Dar ekran: yan menü yerine açılır liste */}
          <select value={bolum} onChange={(e) => bolumSec(e.target.value)}
            className="lg:hidden w-full h-10 rounded-lg border border-[#d0c4b0] bg-white px-3 text-sm"
            data-testid="admin-bolum-secici">
            {BOLUMLER.map(g => (
              <optgroup key={g.grup} label={g.grup}>
                {g.kalemler.map(k => <option key={k.deger} value={k.deger}>{k.ad}</option>)}
              </optgroup>
            ))}
          </select>

          <TabsList className="hidden lg:flex w-60 shrink-0 flex-col items-stretch gap-0.5 border-b-0
                               rounded-xl border border-[#d0c4b0] bg-[#faf7f2] p-2 sticky top-4">
            {BOLUMLER.map(g => (
              <div key={g.grup} className="contents">
                <p className="px-2 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#a3968a] first:pt-1">
                  {g.grup}
                </p>
                {g.kalemler.map(k => (
                  <TabsTrigger key={k.deger} value={k.deger} data-testid={`admin-sekme-${k.deger}`}
                    className="w-full justify-start gap-2 border-b-0 border-l-2 rounded-r-lg px-2.5 py-2 -mb-0
                               text-left text-[13px] leading-tight
                               data-[state=active]:bg-[#e8dfd0] data-[state=active]:font-semibold">
                    <k.ikon size={15} className="shrink-0" />
                    <span className="truncate">{k.ad}</span>
                  </TabsTrigger>
                ))}
              </div>
            ))}
          </TabsList>

          <div className="flex-1 min-w-0 w-full">
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
        <TabsContent value="product-cats"><CategoryTree type="product" title="Ürün Kategorileri" /></TabsContent>
        <TabsContent value="expense-cats"><ExpenseCategoryTree /></TabsContent>
        <TabsContent value="fixed-asset-types"><FixedAssetCategoryTree /></TabsContent>
        <TabsContent value="units"><UnitsTab /></TabsContent>
        <TabsContent value="warehouses"><WarehousesTab /></TabsContent>
        <TabsContent value="merkezler"><SorumlulukMerkeziTab /></TabsContent>
        <TabsContent value="departmanlar"><DepartmanTab /></TabsContent>
        <TabsContent value="companies"><CompaniesTab /></TabsContent>
        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
        <TabsContent value="mcp"><McpKeysTab /></TabsContent>
        <TabsContent value="payroll"><PuantajAyarlariTab /></TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}

// ─── CATEGORY TREE ───
// Türkçe karakterleri sadeleştirir; "İ".toLowerCase() sorunundan dolayı
// düz karşılaştırma "İPLİK" ile "iplik"i eşleştiremiyor.
const trNorm = (s) => (s || '')
  .replace(/[İIı]/g, 'i').replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g')
  .replace(/[öÖ]/g, 'o').replace(/[şŞ]/g, 's').replace(/[üÜ]/g, 'u')
  .toLowerCase();

function CategoryTree({ type, title }) {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const [categories, setCategories] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addParentId, setAddParentId] = useState(null);
  const [addName, setAddName] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [search, setSearch] = useState('');

  const fetch = useCallback(() => {
    api.get(`/admin/categories?type=${type}`).then(r => setCategories(r.data)).catch(() => {});
  }, [type]);
  useEffect(() => { fetch(); }, [fetch]);

  const roots = categories.filter(c => !c.parent_id && c.is_active !== false);
  const getChildren = (id) => categories.filter(c => c.parent_id === id && c.is_active !== false);
  const toggle = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  // Arama: eşleşen düğüm VEYA alt dallarında eşleşme olan düğüm görünür,
  // böylece bir alt kategori bulunduğunda üstündeki yol da görünür kalır.
  const q = trNorm(search.trim());
  const hits = (cat) => !q || trNorm(cat.name).includes(q)
    || getChildren(cat.id).some(hits);
  const matchCount = q ? categories.filter(c => c.is_active !== false && trNorm(c.name).includes(q)).length : 0;

  const addCategory = async () => {
    if (!addName.trim()) return;
    await api.post('/admin/categories', { name: addName, type, parent_id: addParentId });
    toast.success('Kategori eklendi');
    setShowAdd(false); setAddName(''); setAddParentId(null);
    fetch();
  };

  const deactivate = async (id) => {
    if (!window.confirm('Kategoriyi pasife almak istiyor musunuz?\n\nAlt kategoriler ve bağlı kalemler de birlikte pasife alınır.')) return;
    try {
      // Kullanımdaki kayıt varsa backend 400 döner; sebebi kullanıcıya
      // gösterilmezse işlem sessizce başarısız görünüyordu.
      const r = await api.delete(`/admin/categories/${id}`);
      const d = r.data || {};
      const ek = [d.gider_kalemi ? `${d.gider_kalemi} gider kalemi` : null,
                  d.demirbas_turu ? `${d.demirbas_turu} demirbaş türü` : null].filter(Boolean);
      toast.success('Kategori pasife alındı' + (ek.length ? ` (${ek.join(', ')} dahil)` : ''));
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kategori pasife alınamadı');
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const saveEdit = async () => {
    if (!editName.trim() || !editingId) return;
    await api.patch(`/admin/categories/${editingId}`, { name: editName });
    toast.success('Kategori guncellendi');
    setEditingId(null);
    fetch();
  };

  const renderNode = (cat, depth = 0) => {
    if (q && !hits(cat)) return null;
    const children = getChildren(cat.id);
    const hasChildren = children.length > 0;
    // Arama sırasında ağaç açık gösterilir, yoksa sonuç gizli kalır
    const isOpen = q ? true : !collapsed[cat.id];
    const isEditing = editingId === cat.id;

    return (
      <div key={cat.id}>
        <div className={`flex items-center gap-2 py-2 px-3 hover:bg-[#e8dfd0]/30 rounded-md group`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}>
          {hasChildren ? (
            <button onClick={() => toggle(cat.id)} className="text-[#8a7e70]">
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : <span className="w-[14px]" />}

          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingId(null); }}
                className="h-7 text-sm bg-white border-[#d0c4b0] flex-1" autoFocus />
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#3d6e4e]" onClick={saveEdit}><Check size={12} /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70]" onClick={() => setEditingId(null)}><X size={12} /></Button>
            </div>
          ) : (
            <>
              <span className="flex-1 text-sm font-medium text-[#1a1916] cursor-pointer" onDoubleClick={() => startEdit(cat)}>
                {cat.name}
              </span>
              {hasChildren && (
                <span className="text-xs text-[#8a7e70]">{getChildren(cat.id).length} alt</span>
              )}
              <div className="hidden group-hover:flex gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70]" onClick={() => startEdit(cat)}>
                  <Pencil size={11} />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70]"
                  onClick={() => { setAddParentId(cat.id); setShowAdd(true); }}>
                  <Plus size={12} />
                </Button>
                {silmeYetkisi && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70] hover:text-[#7a3535]"
                  onClick={() => deactivate(cat.id)}>
                  <Trash2 size={12} />
                </Button>
                )}
              </div>
            </>
          )}
        </div>
        {hasChildren && isOpen && children.map(c => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916]">{title}</CardTitle>
        <Button size="sm" onClick={() => { setAddParentId(null); setShowAdd(true); }}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          <Plus size={14} className="mr-1" /> Ana Kategori Ekle
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Kategori ara... (eklemeden önce arayın)"
            className="pl-9 pr-8 h-9 text-sm bg-white border-[#d0c4b0]" />
          {search && (
            <button onClick={() => setSearch('')} title="Aramayı temizle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]">
              <X size={14} />
            </button>
          )}
        </div>
        {search && (
          <p className="text-xs text-[#8a7e70] mb-2">
            {matchCount > 0 ? `${matchCount} eşleşme bulundu` : 'Eşleşme yok — bu isimde kategori eklenebilir'}
          </p>
        )}
        {roots.length === 0 ? (
          <p className="text-center py-6 text-[#8a7e70]">Kategori yok</p>
        ) : (
          (() => {
            const nodes = roots.map(c => renderNode(c)).filter(Boolean);
            return nodes.length ? nodes : (
              <p className="text-center py-6 text-[#8a7e70]">Aramaya uyan kategori yok</p>
            );
          })()
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">
            {addParentId ? 'Alt Kategori Ekle' : 'Ana Kategori Ekle'}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Kategori Adi</Label>
              <Input value={addName} onChange={(e) => setAddName(e.target.value)}
                className="bg-white border-[#d0c4b0]" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={addCategory} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── EXPENSE CATEGORY TREE (Gider Ana Kategori > Kategori > Kalem) ───
function ExpenseCategoryTree() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  // Category add dialog
  const [catDialog, setCatDialog] = useState({ open: false, parentId: null, parentName: '' });
  const [catName, setCatName] = useState('');
  // Item add dialog
  const [itemDialog, setItemDialog] = useState({ open: false, categoryId: null, categoryName: '' });
  const [itemForm, setItemForm] = useState({ name: '' });
  // Item edit dialog
  const [itemEditDialog, setItemEditDialog] = useState({ open: false, item: null });
  const [itemEditForm, setItemEditForm] = useState({ name: '' });

  const fetchAll = useCallback(() => {
    api.get('/admin/categories?type=expense').then(r => setCategories(r.data)).catch(() => {});
    api.get('/admin/expense-items').then(r => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const roots = categories.filter(c => !c.parent_id && c.is_active !== false);
  const getChildren = (id) => categories.filter(c => c.parent_id === id && c.is_active !== false);
  const allItems = (catId) => items.filter(it => it.category_id === catId && it.is_active !== false);
  const toggle = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  // Arama hem kategori hem gider kalemi adlarında yapılır; eşleşen kalemin
  // bağlı olduğu kategori yolu görünür kalır.
  const q = trNorm(search.trim());
  const getItems = (catId) => {
    const list = allItems(catId);
    return q ? list.filter(it => trNorm(it.name).includes(q)) : list;
  };
  const hits = (cat) => !q || trNorm(cat.name).includes(q)
    || allItems(cat.id).some(it => trNorm(it.name).includes(q))
    || getChildren(cat.id).some(hits);
  const matchCount = q
    ? categories.filter(c => c.is_active !== false && trNorm(c.name).includes(q)).length
      + items.filter(i => i.is_active !== false && trNorm(i.name).includes(q)).length
    : 0;

  const addCategory = async () => {
    if (!catName.trim()) return;
    await api.post('/admin/categories', { name: catName, type: 'expense', parent_id: catDialog.parentId });
    toast.success('Kategori eklendi');
    setCatDialog({ open: false, parentId: null, parentName: '' }); setCatName('');
    fetchAll();
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Kategoriyi pasife almak istiyor musunuz?\n\nAlt kategoriler ve bağlı gider kalemleri de birlikte pasife alınır.')) return;
    try {
      const r = await api.delete(`/admin/categories/${id}`);
      const d = r.data || {};
      toast.success('Kategori pasife alındı' + (d.gider_kalemi ? ` (${d.gider_kalemi} gider kalemi dahil)` : ''));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kategori pasife alınamadı');
    }
  };

  const saveEditCat = async () => {
    if (!editName.trim()) return;
    await api.patch(`/admin/categories/${editingId}`, { name: editName });
    toast.success('Güncellendi'); setEditingId(null); fetchAll();
  };

  const addItem = async () => {
    if (!itemForm.name.trim()) return;
    await api.post('/admin/expense-items', { name: itemForm.name, category_id: itemDialog.categoryId });
    toast.success('Gider kalemi eklendi');
    setItemDialog({ open: false, categoryId: null, categoryName: '' }); setItemForm({ name: '' });
    fetchAll();
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Gider kalemini pasife almak istiyor musunuz?')) return;
    try {
      await api.delete(`/admin/expense-items/${id}`);
      toast.success('Gider kalemi pasife alındı');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gider kalemi pasife alınamadı');
    }
  };

  const saveEditItem = async () => {
    if (!itemEditForm.name.trim()) return;
    await api.patch(`/admin/expense-items/${itemEditDialog.item.id}`, { name: itemEditForm.name });
    toast.success('Güncellendi'); setItemEditDialog({ open: false, item: null }); fetchAll();
  };

  // depth 0 = Ana Kategori, depth 1 = Kategori, depth 2 = Gider Kalemi seviyesi
  const renderCatNode = (cat, depth = 0) => {
    if (q && !hits(cat)) return null;
    const children = getChildren(cat.id);
    const catItems = getItems(cat.id);
    const hasChildren = children.length > 0;
    const hasItems = catItems.length > 0;
    const isOpen = q ? true : !collapsed[cat.id];
    const isEditing = editingId === cat.id;
    const depthColors = ['font-bold text-[#1a1916]', 'font-semibold text-[#2e2d29]', 'text-[#3F3E39]'];
    const depthBg = ['bg-[#e8dfd0]/60', 'bg-[#f5f0e8]/80', ''];

    return (
      <div key={cat.id}>
        <div className={`flex items-center gap-2 py-2 px-3 rounded-md group ${depthBg[depth] || ''}`}
          style={{ paddingLeft: `${depth * 28 + 12}px` }}>
          {(hasChildren || hasItems) ? (
            <button onClick={() => toggle(cat.id)} className="text-[#8a7e70] shrink-0">
              {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : <span className="w-[13px] shrink-0" />}

          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEditCat(); if (e.key === 'Escape') setEditingId(null); }}
                className="h-7 text-sm bg-white border-[#d0c4b0] flex-1" autoFocus />
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#3d6e4e]" onClick={saveEditCat}><Check size={12} /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70]" onClick={() => setEditingId(null)}><X size={12} /></Button>
            </div>
          ) : (
            <>
              <span className={`flex-1 text-sm cursor-pointer ${depthColors[depth] || 'text-[#3F3E39]'}`}
                onDoubleClick={() => { setEditingId(cat.id); setEditName(cat.name); }}>
                {cat.name}
              </span>
              {(hasChildren || hasItems) && (
                <span className="text-xs text-[#8a7e70]">
                  {hasItems ? `${catItems.length} kalem` : `${children.length} alt kategori`}
                </span>
              )}
              <div className="hidden group-hover:flex gap-1 ml-1">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70]"
                  onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}><Pencil size={11} /></Button>
                {depth < 2 && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#3d6e4e]"
                    title={depth === 1 ? 'Kalem Ekle' : 'Alt Kategori Ekle'}
                    onClick={() => {
                      if (depth === 1) setItemDialog({ open: true, categoryId: cat.id, categoryName: cat.name });
                      else setCatDialog({ open: true, parentId: cat.id, parentName: cat.name });
                    }}><Plus size={12} /></Button>
                )}
                {silmeYetkisi && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70] hover:text-[#7a3535]"
                  onClick={() => deleteCategory(cat.id)}><Trash2 size={11} /></Button>
                )}
              </div>
            </>
          )}
        </div>

        {isOpen && (
          <>
            {children.map(c => renderCatNode(c, depth + 1))}
            {/* Gider Kalemleri (depth=1 altındaki items) */}
            {catItems.map(it => (
              <div key={it.id} className="flex items-center gap-2 py-1.5 px-3 group hover:bg-[#f5f0e8]/50 rounded-md"
                style={{ paddingLeft: `${(depth + 1) * 28 + 12 + 13}px` }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#8a7e70] shrink-0 mt-0.5" />
                <span className="flex-1 text-sm text-[#3F3E39]">
                  {it.name}
                  {/* SISTEM KALEMI: bordro tahakkuku buraya yaziyor. Ad serbest
                      (bag `sistem_kod` uzerinden), ama kaldirilamaz — pasife
                      alinsaydi listede gorunmeyen ama dolu bir kalem kalirdi. */}
                  {it.sistem_kalemi && (
                    <span className="ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold
                                     uppercase bg-[#2e6fa0]/10 text-[#2e6fa0]"
                      title="Sistem kalemi — bordro tahakkuku buraya yazılır. Adını değiştirebilirsiniz.">
                      Sistem
                    </span>
                  )}
                </span>
                <span className="text-xs text-[#8a7e70] font-mono">{it.code}</span>
                <div className="hidden group-hover:flex gap-1">
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70]"
                    onClick={() => { setItemEditDialog({ open: true, item: it }); setItemEditForm({ name: it.name }); }}>
                    <Pencil size={10} /></Button>
                  {!it.sistem_kalemi && silmeYetkisi && (
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70] hover:text-[#7a3535]"
                      onClick={() => deleteItem(it.id)}><Trash2 size={10} /></Button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-bold text-[#1a1916]">Gider Kategorileri</CardTitle>
          <p className="text-xs text-[#8a7e70] mt-0.5">Ana Kategori → Kategori → Gider Kalemi</p>
        </div>
        <Button size="sm" onClick={() => { setCatDialog({ open: true, parentId: null, parentName: '' }); setCatName(''); }}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          <Plus size={14} className="mr-1" /> Ana Kategori Ekle
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-0.5">
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Kategori veya gider kalemi ara... (eklemeden önce arayın)"
            className="pl-9 pr-8 h-9 text-sm bg-white border-[#d0c4b0]" />
          {search && (
            <button onClick={() => setSearch('')} title="Aramayı temizle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]">
              <X size={14} />
            </button>
          )}
        </div>
        {search && (
          <p className="text-xs text-[#8a7e70] mb-2">
            {matchCount > 0 ? `${matchCount} eşleşme bulundu` : 'Eşleşme yok — bu isimde kayıt eklenebilir'}
          </p>
        )}
        {roots.length === 0
          ? <p className="text-center py-8 text-[#8a7e70]">Henüz kategori yok</p>
          : (() => {
              const nodes = roots.map(c => renderCatNode(c, 0)).filter(Boolean);
              return nodes.length ? nodes : (
                <p className="text-center py-8 text-[#8a7e70]">Aramaya uyan kayıt yok</p>
              );
            })()}
      </CardContent>

      {/* Kategori Ekle Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(v) => setCatDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {catDialog.parentId ? `Alt Kategori Ekle` : 'Ana Kategori Ekle'}
            </DialogTitle>
            {catDialog.parentName && <p className="text-xs text-[#8a7e70]">Üst: {catDialog.parentName}</p>}
          </DialogHeader>
          <div className="space-y-3">
            <Input value={catName} onChange={(e) => setCatName(e.target.value)}
              placeholder="Kategori adı" className="bg-white border-[#d0c4b0]" autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog({ open: false, parentId: null, parentName: '' })} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={addCategory} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gider Kalemi Ekle Dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(v) => setItemDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Gider Kalemi Ekle</DialogTitle>
            {itemDialog.categoryName && <p className="text-xs text-[#8a7e70]">Kategori: {itemDialog.categoryName}</p>}
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#3F3E39]">Kalem Adı *</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Elektrik Faturası" className="bg-white border-[#d0c4b0]" autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') addItem(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog({ open: false, categoryId: null, categoryName: '' })} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={addItem} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gider Kalemi Düzenle Dialog */}
      <Dialog open={itemEditDialog.open} onOpenChange={(v) => setItemEditDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Gider Kalemi Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#3F3E39]">Kalem Adı *</Label>
              <Input value={itemEditForm.name} onChange={(e) => setItemEditForm(p => ({ ...p, name: e.target.value }))}
                className="bg-white border-[#d0c4b0]" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveEditItem(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemEditDialog({ open: false, item: null })} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={saveEditItem} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── UNITS TAB ───
function UnitsTab() {
  const [units, setUnits] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', abbreviation: '', unit_group: 'quantity', to_base_factor: '1' });

  useEffect(() => { api.get('/admin/units').then(r => setUnits(r.data)).catch(() => {}); }, []);

  const addUnit = async () => {
    if (!form.name.trim() || !form.abbreviation.trim()) return;
    await api.post('/units', { ...form, to_base_factor: parseFloat(form.to_base_factor) });
    toast.success('Birim eklendi');
    setShowAdd(false); setForm({ name: '', abbreviation: '', unit_group: 'quantity', to_base_factor: '1' });
    api.get('/admin/units').then(r => setUnits(r.data));
  };

  const groupLabels = { weight: 'Agirlik', volume: 'Hacim', area: 'Alan', length: 'Uzunluk', quantity: 'Miktar' };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916]">Birimler</CardTitle>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          <Plus size={14} className="mr-1" /> Yeni Birim
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kisaltma</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Grup</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Base Factor</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Base?</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {units.map(u => (
              <TableRow key={u.id} className="border-[#d0c4b0]">
                <TableCell className="font-mono font-semibold text-[#1a1916]">{u.abbreviation}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{u.name}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{groupLabels[u.unit_group] || u.unit_group}</TableCell>
                <TableCell className="text-right font-mono text-sm">{u.to_base_factor}</TableCell>
                <TableCell>{u.base_unit ? <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Base</span> : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Birim</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Ad</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="space-y-2"><Label>Kisaltma</Label>
                <Input value={form.abbreviation} onChange={(e) => setForm(p => ({ ...p, abbreviation: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Grup</Label>
                <Select value={form.unit_group} onValueChange={(v) => setForm(p => ({ ...p, unit_group: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weight">Agirlik</SelectItem>
                    <SelectItem value="volume">Hacim</SelectItem>
                    <SelectItem value="area">Alan</SelectItem>
                    <SelectItem value="length">Uzunluk</SelectItem>
                    <SelectItem value="quantity">Miktar</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Base Factor</Label>
                <Input type="number" step="0.001" value={form.to_base_factor} onChange={(e) => setForm(p => ({ ...p, to_base_factor: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={addUnit} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── WAREHOUSES TAB ───
function WarehousesTab() {
  const [warehouses, setWarehouses] = useState([]);
  useEffect(() => { api.get('/admin/warehouses').then(r => setWarehouses(r.data)).catch(() => {}); }, []);
  const whTypes = { main: 'Ana Depo', kitchen: 'Mutfak', bar: 'Bar', cold_storage: 'Soguk Depo', other: 'Diger' };
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0]"><CardTitle className="text-lg font-bold text-[#1a1916]">Depolar</CardTitle></CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo Adi</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tur</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {warehouses.map(w => (
              <TableRow key={w.id} className="border-[#d0c4b0]">
                <TableCell className="font-medium text-[#1a1916]">{w.name}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{whTypes[w.type] || w.type}</TableCell>
                <TableCell><span className={`status-badge ${w.is_active ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a3535]/10 text-[#7a3535]'}`}>
                  {w.is_active ? 'Aktif' : 'Pasif'}
                </span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── COMPANY TAB ───
function CompaniesTab() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', address_country: 'TR', tax_number: '', enabled_modules: DEFAULT_ENABLED_MODULES });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/companies');
      setCompanies(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', address: '', address_country: 'TR', tax_number: '', enabled_modules: DEFAULT_ENABLED_MODULES });
    setEditingCompany(null);
  };

  const toggleModule = (key) => {
    setForm((f) => ({
      ...f,
      enabled_modules: f.enabled_modules.includes(key)
        ? f.enabled_modules.filter((k) => k !== key)
        : [...f.enabled_modules, key],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Şirket adı zorunlu', { duration: 5000 });
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/companies', form);
      toast.success('Şirket oluşturuldu');
      fetchCompanies();
      setShowNew(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) {
      toast.error('Şirket adı zorunlu', { duration: 5000 });
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/admin/companies/${editingCompany.id}`, form);
      toast.success('Şirket güncellendi');
      fetchCompanies();
      setShowEdit(false);
      setEditingCompany(null);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setForm({
      name: company.name || '',
      address: company.address || '',
      address_country: company.address_country || 'TR',
      tax_number: company.tax_number || '',
      // Alan hiç yoksa (eski şirket) bugün zaten HER modülü görüyor —
      // formda da hepsi işaretli gelmeli, yoksa boş kaydedilip şirket
      // her şeyi kaybeder (bkz. plan notu).
      enabled_modules: Array.isArray(company.enabled_modules)
        ? company.enabled_modules
        : MODULE_CATALOG.map((m) => m.key),
    });
    setShowEdit(true);
  };

  const getCountryName = (code) => {
    const country = COUNTRIES_SORTED.find(c => c.code === code);
    return country ? country.name : code;
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-xl font-bold text-[#1a1916] flex items-center gap-2">
          <Building2 size={20} /> Şirketler
        </CardTitle>
        <Button onClick={() => { resetForm(); setShowNew(true); }} 
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"
          data-testid="new-company-btn">
          <Plus size={16} className="mr-2" /> Yeni Şirket Ekle
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Şirket Adı</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Vergi No</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ülke</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Modüller</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map(c => (
              <TableRow key={c.id} className="border-[#d0c4b0] hover:bg-[#f5f0e8]">
                <TableCell className="font-medium text-[#1a1916]">{c.name}</TableCell>
                <TableCell className="font-mono text-sm text-[#3F3E39]">{c.tax_number || '-'}</TableCell>
                <TableCell className="text-sm text-[#3F3E39]">{getCountryName(c.address_country)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {Array.isArray(c.enabled_modules) ? (
                      c.enabled_modules.length > 0 ? (
                        c.enabled_modules.map((key) => (
                          <Badge key={key} variant="secondary" className="text-xs bg-[#e8dfd0] text-[#3F3E39]">
                            {MODULE_CATALOG.find((m) => m.key === key)?.label || key}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-[#8a7e70]">Hiçbiri</span>
                      )
                    ) : (
                      <span className="text-xs text-[#8a7e70]">Tümü (varsayılan)</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={c.is_active !== false
                    ? 'bg-[#3d6e4e]/10 text-[#3d6e4e] hover:bg-[#3d6e4e]/20' 
                    : 'bg-[#7a3535]/10 text-[#7a3535] hover:bg-[#7a3535]/20'}>
                    {c.is_active !== false ? 'Aktif' : 'Pasif'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                    <Pencil size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">
                  {loading ? 'Yükleniyor...' : 'Henüz şirket yok'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* New Company Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <Building2 size={20} /> Yeni Şirket Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Şirket Adı *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
                placeholder="Şirket adını girin"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Vergi No</Label>
              <Input 
                value={form.tax_number} 
                onChange={(e) => setForm(f => ({ ...f, tax_number: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
                placeholder="1234567890"
                data-testid="company-tax-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Adres</Label>
              <Input 
                value={form.address} 
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
                placeholder="Şirket adresi"
                data-testid="company-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Ülke</Label>
              <Select value={form.address_country} onValueChange={(v) => setForm(f => ({ ...f, address_country: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="company-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Modüller</Label>
              <p className="text-xs text-[#8a7e70]">
                Bu şirkette hangi modüllerin kullanılacağını seçin — menüde sadece seçilenler görünür.
              </p>
              <div className="border border-[#d0c4b0] rounded-lg bg-white p-3 space-y-2">
                {MODULE_CATALOG.map((m) => (
                  <div key={m.key} className="flex items-center gap-2 cursor-pointer" onClick={() => toggleModule(m.key)}>
                    <Checkbox checked={form.enabled_modules.includes(m.key)} />
                    <span className="text-sm text-[#1a1916]">{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleCreate} disabled={loading}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
              data-testid="save-company-btn">
              {loading ? 'Kaydediliyor...' : 'Şirket Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <Building2 size={20} /> Şirket Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Şirket Adı *</Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Vergi No</Label>
              <Input 
                value={form.tax_number} 
                onChange={(e) => setForm(f => ({ ...f, tax_number: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Adres</Label>
              <Input 
                value={form.address} 
                onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} 
                className="bg-white border-[#d0c4b0]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Ülke</Label>
              <Select value={form.address_country} onValueChange={(v) => setForm(f => ({ ...f, address_country: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Modüller</Label>
              <p className="text-xs text-[#8a7e70]">
                Bu şirkette hangi modüllerin kullanılacağını seçin — menüde sadece seçilenler görünür.
              </p>
              <div className="border border-[#d0c4b0] rounded-lg bg-white p-3 space-y-2">
                {MODULE_CATALOG.map((m) => (
                  <div key={m.key} className="flex items-center gap-2 cursor-pointer" onClick={() => toggleModule(m.key)}>
                    <Checkbox checked={form.enabled_modules.includes(m.key)} />
                    <span className="text-sm text-[#1a1916]">{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleUpdate} disabled={loading}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              {loading ? 'Kaydediliyor...' : 'Güncelle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// =====================
// FIXED ASSET TYPES TAB (Demirbaş Türleri)
// =====================
// ─── FIXED ASSET CATEGORY TREE (Ana Kategori > Kategori > Demirbaş Türü) ───
function FixedAssetCategoryTree() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [catDialog, setCatDialog] = useState({ open: false, parentId: null, parentName: '' });
  const [catName, setCatName] = useState('');
  const [typeDialog, setTypeDialog] = useState({ open: false, categoryId: null, categoryName: '', editItem: null });
  const [typeForm, setTypeForm] = useState({ name: '', description: '', depreciation_rate: '20', useful_life_years: '5', account_code: '' });

  const fetchAll = useCallback(() => {
    api.get('/admin/categories?type=fixed_asset').then(r => setCategories(r.data)).catch(() => {});
    api.get('/fixed-asset-types').then(r => setTypes(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const roots = categories.filter(c => !c.parent_id && c.is_active !== false);
  const getChildren = (id) => categories.filter(c => c.parent_id === id && c.is_active !== false);
  const allTypes = (catId) => types.filter(t => t.category_id === catId && t.is_active !== false);
  const toggle = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  // Arama kategori adında ve demirbaş türü ad/kodunda yapılır.
  const q = trNorm(search.trim());
  const typeHit = (t) => trNorm(t.name).includes(q) || trNorm(t.code || '').includes(q);
  const getTypes = (catId) => {
    const list = allTypes(catId);
    return q ? list.filter(typeHit) : list;
  };
  const hits = (cat) => !q || trNorm(cat.name).includes(q)
    || allTypes(cat.id).some(typeHit)
    || getChildren(cat.id).some(hits);
  const matchCount = q
    ? categories.filter(c => c.is_active !== false && trNorm(c.name).includes(q)).length
      + types.filter(t => t.is_active !== false && typeHit(t)).length
    : 0;

  const addCategory = async () => {
    if (!catName.trim()) return;
    await api.post('/admin/categories', { name: catName, type: 'fixed_asset', parent_id: catDialog.parentId });
    toast.success('Kategori eklendi');
    setCatDialog({ open: false, parentId: null, parentName: '' }); setCatName(''); fetchAll();
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Kategoriyi pasife almak istiyor musunuz?\n\nAlt kategoriler ve bağlı demirbaş türleri de birlikte pasife alınır.')) return;
    try {
      const r = await api.delete(`/admin/categories/${id}`);
      const d = r.data || {};
      toast.success('Kategori pasife alındı' + (d.demirbas_turu ? ` (${d.demirbas_turu} demirbaş türü dahil)` : ''));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kategori pasife alınamadı');
    }
  };

  const saveEditCat = async () => {
    if (!editName.trim()) return;
    await api.patch(`/admin/categories/${editingId}`, { name: editName });
    toast.success('Güncellendi'); setEditingId(null); fetchAll();
  };

  const openTypeDialog = (catId, catName, editItem = null) => {
    setTypeDialog({ open: true, categoryId: catId, categoryName: catName, editItem });
    if (editItem) {
      setTypeForm({ name: editItem.name, description: editItem.description || '', depreciation_rate: String(editItem.depreciation_rate || 20), useful_life_years: String(editItem.useful_life_years || 5), account_code: editItem.account_code || '' });
    } else {
      setTypeForm({ name: '', description: '', depreciation_rate: '20', useful_life_years: '5', account_code: '' });
    }
  };

  const saveType = async () => {
    if (!typeForm.name.trim()) return;
    const payload = { name: typeForm.name, description: typeForm.description || null, depreciation_rate: parseFloat(typeForm.depreciation_rate) || 20, useful_life_years: parseInt(typeForm.useful_life_years) || 5, account_code: typeForm.account_code || null, category_id: typeDialog.categoryId };
    try {
      if (typeDialog.editItem) {
        await api.put(`/fixed-asset-types/${typeDialog.editItem.id}`, payload);
        toast.success('Demirbaş türü güncellendi');
      } else {
        // Auto-generate code
        const existing = types.filter(t => t.code?.startsWith('DMB-'));
        const maxNum = existing.reduce((m, t) => { try { return Math.max(m, parseInt(t.code.split('-')[1])); } catch { return m; } }, 0);
        await api.post('/fixed-asset-types', { ...payload, code: `DMB-${String(maxNum + 1).zfill ? String(maxNum + 1).padStart(3, '0') : String(maxNum + 1).padStart(3, '0')}` });
        toast.success('Demirbaş türü eklendi');
      }
      setTypeDialog({ open: false, categoryId: null, categoryName: '', editItem: null }); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const deleteType = async (id) => {
    if (!window.confirm('Demirbaş türünü silmek istiyor musunuz?')) return;
    try {
      await api.delete(`/fixed-asset-types/${id}`);
      toast.success('Demirbaş türü silindi');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demirbaş türü silinemedi');
    }
  };

  const renderCatNode = (cat, depth = 0) => {
    if (q && !hits(cat)) return null;
    const children = getChildren(cat.id);
    const catTypes = getTypes(cat.id);
    const hasChildren = children.length > 0;
    const hasTypes = catTypes.length > 0;
    const isOpen = q ? true : !collapsed[cat.id];
    const isEditing = editingId === cat.id;
    const depthColors = ['font-bold text-[#1a1916]', 'font-semibold text-[#2e2d29]'];
    const depthBg = ['bg-[#e8dfd0]/60', 'bg-[#f5f0e8]/80'];

    return (
      <div key={cat.id}>
        <div className={`flex items-center gap-2 py-2 px-3 rounded-md group ${depthBg[depth] || ''}`}
          style={{ paddingLeft: `${depth * 28 + 12}px` }}>
          {(hasChildren || hasTypes) ? (
            <button onClick={() => toggle(cat.id)} className="text-[#8a7e70] shrink-0">
              {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
          ) : <span className="w-[13px] shrink-0" />}

          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEditCat(); if (e.key === 'Escape') setEditingId(null); }}
                className="h-7 text-sm bg-white border-[#d0c4b0] flex-1" autoFocus />
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#3d6e4e]" onClick={saveEditCat}><Check size={12} /></Button>
              <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70]" onClick={() => setEditingId(null)}><X size={12} /></Button>
            </div>
          ) : (
            <>
              <span className={`flex-1 text-sm cursor-pointer ${depthColors[depth] || 'text-[#3F3E39]'}`}
                onDoubleClick={() => { setEditingId(cat.id); setEditName(cat.name); }}>
                {cat.name}
              </span>
              {(hasChildren || hasTypes) && (
                <span className="text-xs text-[#8a7e70]">{hasTypes ? `${catTypes.length} tür` : `${children.length} alt kategori`}</span>
              )}
              <div className="hidden group-hover:flex gap-1 ml-1">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70]"
                  onClick={() => { setEditingId(cat.id); setEditName(cat.name); }}><Pencil size={11} /></Button>
                {depth < 2 && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-[#3d6e4e]"
                    title={depth === 1 ? 'Tür Ekle' : 'Alt Kategori Ekle'}
                    onClick={() => {
                      if (depth === 1) openTypeDialog(cat.id, cat.name);
                      else setCatDialog({ open: true, parentId: cat.id, parentName: cat.name });
                    }}><Plus size={12} /></Button>
                )}
                {silmeYetkisi && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8a7e70] hover:text-[#7a3535]"
                  onClick={() => deleteCategory(cat.id)}><Trash2 size={11} /></Button>
                )}
              </div>
            </>
          )}
        </div>

        {isOpen && (
          <>
            {children.map(c => renderCatNode(c, depth + 1))}
            {catTypes.map(t => (
              <div key={t.id} className="flex items-center gap-2 py-1.5 px-3 group hover:bg-[#f5f0e8]/50 rounded-md"
                style={{ paddingLeft: `${(depth + 1) * 28 + 12 + 13}px` }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#8a7e70] shrink-0 mt-0.5" />
                <span className="flex-1 text-sm text-[#3F3E39]">{t.name}</span>
                <span className="text-xs text-[#8a7e70] font-mono">{t.code}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-[#e8dfd0] text-[#3F3E39]">%{t.depreciation_rate} / {t.useful_life_years}yıl</span>
                <div className="hidden group-hover:flex gap-1">
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70]"
                    onClick={() => openTypeDialog(t.category_id, '', t)}><Pencil size={10} /></Button>
                  {silmeYetkisi && (
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-[#8a7e70] hover:text-[#7a3535]"
                    onClick={() => deleteType(t.id)}><Trash2 size={10} /></Button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-bold text-[#1a1916]">Demirbaş Türleri</CardTitle>
          <p className="text-xs text-[#8a7e70] mt-0.5">Ana Kategori → Kategori → Demirbaş Türü</p>
        </div>
        <Button size="sm" onClick={() => { setCatDialog({ open: true, parentId: null, parentName: '' }); setCatName(''); }}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          <Plus size={14} className="mr-1" /> Ana Kategori Ekle
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-0.5">
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Kategori veya demirbaş türü ara... (eklemeden önce arayın)"
            className="pl-9 pr-8 h-9 text-sm bg-white border-[#d0c4b0]" />
          {search && (
            <button onClick={() => setSearch('')} title="Aramayı temizle"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]">
              <X size={14} />
            </button>
          )}
        </div>
        {search && (
          <p className="text-xs text-[#8a7e70] mb-2">
            {matchCount > 0 ? `${matchCount} eşleşme bulundu` : 'Eşleşme yok — bu isimde kayıt eklenebilir'}
          </p>
        )}
        {roots.length === 0
          ? <p className="text-center py-8 text-[#8a7e70]">Henüz kategori yok</p>
          : (() => {
              const nodes = roots.map(c => renderCatNode(c, 0)).filter(Boolean);
              return nodes.length ? nodes : (
                <p className="text-center py-8 text-[#8a7e70]">Aramaya uyan kayıt yok</p>
              );
            })()}
      </CardContent>

      {/* Kategori Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(v) => setCatDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {catDialog.parentId ? 'Alt Kategori Ekle' : 'Ana Kategori Ekle'}
            </DialogTitle>
            {catDialog.parentName && <p className="text-xs text-[#8a7e70]">Üst: {catDialog.parentName}</p>}
          </DialogHeader>
          <Input value={catName} onChange={(e) => setCatName(e.target.value)}
            placeholder="Kategori adı" className="bg-white border-[#d0c4b0]" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog({ open: false, parentId: null, parentName: '' })} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={addCategory} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tür Ekle/Düzenle Dialog */}
      <Dialog open={typeDialog.open} onOpenChange={(v) => setTypeDialog(p => ({ ...p, open: v }))}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">{typeDialog.editItem ? 'Demirbaş Türü Düzenle' : 'Demirbaş Türü Ekle'}</DialogTitle>
            {typeDialog.categoryName && <p className="text-xs text-[#8a7e70]">Kategori: {typeDialog.categoryName}</p>}
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#3F3E39]">Ad *</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Masa" className="bg-white border-[#d0c4b0]" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#3F3E39]">Açıklama</Label>
              <Input value={typeForm.description} onChange={(e) => setTypeForm(p => ({ ...p, description: e.target.value }))}
                className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#3F3E39]">Amortisman %</Label>
                <Input type="number" value={typeForm.depreciation_rate} onChange={(e) => setTypeForm(p => ({ ...p, depreciation_rate: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#3F3E39]">Ömür (yıl)</Label>
                <Input type="number" value={typeForm.useful_life_years} onChange={(e) => setTypeForm(p => ({ ...p, useful_life_years: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#3F3E39]">Hesap Kodu</Label>
                <Input value={typeForm.account_code} onChange={(e) => setTypeForm(p => ({ ...p, account_code: e.target.value }))}
                  placeholder="255.01" className="bg-white border-[#d0c4b0] font-mono" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialog({ open: false, categoryId: null, categoryName: '', editItem: null })} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={saveType} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">{typeDialog.editItem ? 'Güncelle' : 'Ekle'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ─── YETKİ MATRİSİNİN SAYFALARI — rol ve kullanıcı ekranı ORTAK ───
// Anahtar, sunucunun `require_permission` / `module_permission` ile GERÇEKTEN
// kontrol ettiği sayfa adı olmalı. Liste bir dönem iki ayrı kopyaydı ve ikisinde
// de Dosya Kutusu, Satış Faturaları ve inşaat sayfaları YOKTU: sunucu onları
// kontrol ediyor ama matriste satır olmadığı için kimseye verilemiyordu —
// fiilen yalnızca süper admin erişebiliyordu (kullanıcı, 2026-09-28: "Dosya
// kutusuna kimler girebiliyor? Yetkilendirmede böyle bir sistem göremedim").
// Yeni bir sayfa yetkisi eklerken buraya da satır ekle.
const PERMISSION_PAGES = [
  { key: 'dashboard', label: 'Kontrol Paneli' },
  { key: 'products', label: 'Ürün Kartları' },
  { key: 'warehouses', label: 'Depolar' },
  { key: 'stock_movements', label: 'Stok Hareketleri' },
  { key: 'stock_counts', label: 'Sayım' },
  { key: 'waste', label: 'Fire / Zayi' },
  // Uçlarda zorunlu ama matriste yoktu: kimseye verilemiyordu, yalnızca
  // süper admin erişebiliyordu.
  { key: 'stock_quantity', label: 'Stok Miktarını Görme' },
  { key: 'consumptions', label: 'Tüketim Kayıtları' },
  { key: 'purchasing', label: 'Sipariş ve Mal Kabul' },
  { key: 'dosya_kutusu', label: 'Dosya Kutusu' },
  { key: 'recipes', label: 'Reçeteler' },
  { key: 'production_orders', label: 'Üretim Emirleri' },
  { key: 'sales', label: 'Ürün Satış Raporu' },
  { key: 'satis_faturalari', label: 'Satış Faturaları' },
  { key: 'treasury', label: 'Hesaplar ve Hareketler' },
  { key: 'suppliers', label: 'Cari Hesaplar' },
  { key: 'invoices', label: 'Alış Faturaları' },
  { key: 'payment_calendar', label: 'Ödeme Takvimi' },
  { key: 'employees', label: 'Personeller' },
  { key: 'timesheet', label: 'Puantaj' },
  { key: 'insaat_sites', label: 'İnşaat: Şantiyeler' },
  { key: 'insaat_subcontractors', label: 'İnşaat: Alt Yükleniciler' },
  { key: 'insaat_reports', label: 'İnşaat: Günlük Raporlar' },
  { key: 'whatsapp', label: 'WhatsApp Bildirimleri' },
  { key: 'admin', label: 'Admin Paneli' },
  { key: 'pos_integration', label: 'POS Entegrasyonu' },
];
const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];

// ─── USERS TAB ───

function UsersTab() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  const [showCustomPerms, setShowCustomPerms] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', role: 'personel', employee_id: '', cost_center_ids: [],
    // Yeni kullanıcı kapalı başlar: yanlışlıkla herkesin maaşını açmak pahalı
    personel_kapsam: 'yok', custom_permissions: null
  });
  const [addExistingForm, setAddExistingForm] = useState({ email: '', role: 'personel' });
  const [addExistingLoading, setAddExistingLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, employeesRes, companiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/roles/list'),
        api.get('/hr/employees'),
        api.get('/admin/companies')
      ]);
      setUsers(usersRes.data || []);
      setRoles(rolesRes.data || []);
      setEmployees(employeesRes.data || []);
      setCompanies(companiesRes.data || []);
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const companyName = (companyId) => companies.find(c => c.id === companyId)?.name || 'Başka şirket';

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (emp) {
      setForm(f => ({
        ...f,
        employee_id: empId,
        name: `${emp.first_name} ${emp.last_name}`,
        email: emp.email || ''
      }));
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.email) {
      toast.error('Ad ve email zorunlu', { duration: 5000 });
      return;
    }
    try {
      const res = await api.post('/users', form);
      setTempPassword(res.data.temp_password);
      toast.success('Kullanıcı oluşturuldu');
      fetchAll();
      setShowNew(false);
      setForm({ name: '', email: '', role: 'personel', employee_id: '', cost_center_ids: [],
        personel_kapsam: 'yok', custom_permissions: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    }
  };

  const handleUpdate = async () => {
    try {
      await api.patch(`/users/${editingUser.id}`, form);
      toast.success('Kullanıcı güncellendi');
      fetchAll();
      setShowEdit(false);
      setEditingUser(null);
      setShowCustomPerms(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await api.post(`/users/${userId}/reset-password`);
      setTempPassword(res.data.temp_password);
      toast.success('Şifre sıfırlandı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    }
  };

  const handleUnlock = async (userId) => {
    try {
      await api.post(`/users/${userId}/unlock`);
      toast.success('Hesap kilidi açıldı');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    }
  };

  const handleDelete = async (u) => {
    // DELETE /users/{id} aslinda sadece BU sirketten erisimi kaldiriyor;
    // baska sirketi kalmadiysa hesabi tamamen siliyor (backend/routes/users.py).
    // Onay mesaji hangisinin olacagini net soylemeli.
    const mesaj = (u.company_access?.length || 0) > 1
      ? `${u.name} kullanıcısının SADECE bu şirketten erişimi kaldırılacak, diğer şirketlerdeki erişimi korunacak. Emin misiniz?`
      : `${u.name} kullanıcısının başka şirkete erişimi olmadığı için hesabı TAMAMEN silinecek. Emin misiniz?`;
    if (!confirm(mesaj)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('İşlem tamamlandı');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    }
  };

  const handleAddExisting = async () => {
    if (!addExistingForm.email.trim()) {
      toast.error('E-posta zorunlu', { duration: 5000 });
      return;
    }
    setAddExistingLoading(true);
    try {
      const res = await api.post('/users/add-existing', addExistingForm);
      toast.success(`${res.data.name} bu şirkete eklendi`);
      setShowAddExisting(false);
      setAddExistingForm({ email: '', role: 'personel' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu', { duration: 5000 });
    } finally {
      setAddExistingLoading(false);
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      name: u.name,
      email: u.email,
      role: u.role || 'viewer',
      employee_id: u.employee_id || '',
      cost_center_ids: u.cost_center_ids || [],
      personel_kapsam: u.personel_kapsam || 'tumu',
      custom_permissions: u.custom_permissions || null
    });
    setShowCustomPerms(!!u.custom_permissions);
    setShowEdit(true);
  };

  // Rol seçildiğinde rol şablonunun yetkilerini yükle
  const handleRoleChange = (roleCode) => {
    setForm(f => ({ ...f, role: roleCode }));
    // Custom permissions aktifse, rol şablonunu başlangıç noktası olarak kullan
    if (showCustomPerms) {
      const selectedRole = roles.find(r => r.code === roleCode);
      if (selectedRole) {
        setForm(f => ({ ...f, custom_permissions: { ...selectedRole.permissions } }));
      }
    }
  };

  // Custom permissions toggle
  const toggleCustomPerms = (enabled) => {
    setShowCustomPerms(enabled);
    if (enabled) {
      // Rol şablonundan kopyala
      const selectedRole = roles.find(r => r.code === form.role);
      setForm(f => ({ ...f, custom_permissions: selectedRole?.permissions ? { ...selectedRole.permissions } : {} }));
    } else {
      // Custom permissions'ı temizle
      setForm(f => ({ ...f, custom_permissions: null }));
    }
  };

  // Permission toggle for custom permissions
  const toggleUserPermission = (page, action) => {
    setForm(f => {
      const perms = { ...(f.custom_permissions || {}) };
      if (!perms[page]) perms[page] = [];
      
      if (perms[page].includes(action)) {
        perms[page] = perms[page].filter(a => a !== action);
      } else {
        perms[page] = [...perms[page], action];
      }
      
      // ANAHTARI SİLME. Boş dizi "bu sayfa KAPALI" demek ve rol şablonunu ezer;
      // anahtar silinirse sayfa şablondan geri gelir, yani işareti kaldırmak
      // yetkiyi kapatmak yerine AÇIK bırakır. Ülgen Oskay'a yalnızca Personel ve
      // Puantaj verilmişti ama `viewer` şablonu üzerinden bütün sayfaları
      // görüyordu (2026-09-18).
      return { ...f, custom_permissions: perms };
    });
  };

  // Permission matrix for user edit
  // Ayni sokme-kurma sorunu burada da vardi; ortak bilesen kullaniliyor.
  const kullaniciMatrisi = (
    <PermissionMatrix
      pages={PERMISSION_PAGES}
      actions={PERMISSION_ACTIONS}
      permissions={form.custom_permissions || {}}
      onToggle={toggleUserPermission}
    />
  );

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Users size={18} /> Kullanıcılar
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddExisting(true)} className="border-[#d0c4b0]" data-testid="add-existing-user-btn">
            <Plus size={16} className="mr-2" /> Mevcut Kullanıcıyı Ekle
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="add-user-btn">
            <Plus size={16} className="mr-2" /> Yeni Kullanıcı
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Email</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Rol</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Şirketler</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Son Giriş</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Son IP</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} className="hover:bg-[#f5f0e8]">
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="bg-[#e8dfd0] text-[#1a1916]">{u.role_name || u.role}</Badge>
                    {/* Özel yetkili kullanıcı rol şablonundan KOPUK: şablon
                        değişince elle güncellenmeli, bu yüzden işaretli. */}
                    {u.ozel_yetki && (
                      <Badge variant="outline" className="bg-[#7a6a2e]/10 text-[#5c554b] border-[#7a6a2e]/30"
                        title="Rol şablonu uygulanmaz; yetkiler kullanıcıya özel tanımlı"
                        data-testid={`ozel-yetki-${u.id}`}>
                        Özel yetki
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-[200px]">
                    {(u.company_access || []).map((a) => (
                      <Badge key={a.company_id} variant="secondary" className="text-xs bg-[#e8dfd0] text-[#3F3E39]">
                        {companyName(a.company_id)}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString('tr-TR') : '-'}
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70] font-mono">{u.last_login_ip || '-'}</TableCell>
                <TableCell>
                  {u.locked_until ? (
                    <Badge className="bg-red-100 text-red-800">Kilitli</Badge>
                  ) : u.is_temporary_password ? (
                    <Badge className="bg-yellow-100 text-yellow-800">Geçici Şifre</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800">Aktif</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {u.locked_until && (
                    <Button variant="ghost" size="sm" onClick={() => handleUnlock(u.id)} className="text-green-600">
                      <Unlock size={14} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id)} className="text-[#8a7e70]">
                    <Key size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-[#8a7e70]">
                    <Pencil size={14} />
                  </Button>
                  {u.id !== currentUser?.id && silmeYetkisi && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(u)} className="text-red-600">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-[#8a7e70]">
                  Kullanıcı bulunamadı
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* New User Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Yeni Kullanıcı</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              Kullanıcı oluşturulunca sistem geçici şifre üretecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Personelden Seç (Opsiyonel)</Label>
              <Select value={form.employee_id} onValueChange={handleEmployeeSelect}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue placeholder="Personel seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Ad Soyad *</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Email *</Label>
              <Input 
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Personel Görünürlüğü</Label>
              <Select value={form.personel_kapsam}
                onValueChange={(v) => setForm(f => ({ ...f, personel_kapsam: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="user-kapsam-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KAPSAM_SECENEKLERI.map(k => (
                    <SelectItem key={k.deger} value={k.deger}>{k.etiket}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8a7e70]">
                {KAPSAM_ACIKLAMA[form.personel_kapsam]}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleCreate} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="save-user-btn">
              Kullanıcı Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Existing User Dialog */}
      <Dialog open={showAddExisting} onOpenChange={setShowAddExisting}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Mevcut Kullanıcıyı Ekle</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              Başka bir şirkette kayıtlı bir kullanıcıyı, bu şirkete de ekler
              (örn. birden fazla şirkette çalışan muhasebe personeli).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">E-posta *</Label>
              <Input
                type="email"
                value={addExistingForm.email}
                onChange={(e) => setAddExistingForm(f => ({ ...f, email: e.target.value }))}
                placeholder="kullanici@ornek.com"
                className="bg-white border-[#d0c4b0]"
                data-testid="add-existing-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Bu şirketteki rolü</Label>
              <Select value={addExistingForm.role} onValueChange={(v) => setAddExistingForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="add-existing-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExisting(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleAddExisting} disabled={addExistingLoading}
              className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="save-add-existing-btn">
              {addExistingLoading ? 'Ekleniyor...' : 'Şirkete Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) setShowCustomPerms(false); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Kullanıcı Düzenle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Ad Soyad</Label>
                <Input 
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-white border-[#d0c4b0]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Email</Label>
                <Input 
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-white border-[#d0c4b0]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Rol Şablonu</Label>
              <Select value={form.role} onValueChange={handleRoleChange}>
                <SelectTrigger className="bg-white border-[#d0c4b0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Personel Görünürlüğü</Label>
              <Select value={form.personel_kapsam}
                onValueChange={(v) => setForm(f => ({ ...f, personel_kapsam: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="user-kapsam-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KAPSAM_SECENEKLERI.map(k => (
                    <SelectItem key={k.deger} value={k.deger}>{k.etiket}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8a7e70]">
                {KAPSAM_ACIKLAMA[form.personel_kapsam]}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Personel Kaydı</Label>
              <Select value={form.employee_id || 'yok'}
                onValueChange={(v) => setForm(f => ({ ...f, employee_id: v === 'yok' ? '' : v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="user-personel-select">
                  <SelectValue placeholder="Personel seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yok">— personele bağlı değil —</SelectItem>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8a7e70]">
                Departman sorumluluğu bu bağ üzerinden çözülür; bağ yoksa
                "sorumlusu olduğu departman" kapsamı hiçbir personel getirmez.
              </p>
            </div>

            {/* Custom Permissions Toggle */}
            <div className="border-t border-[#d0c4b0] pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-[#3F3E39] font-medium">Yetki Özelleştirme</Label>
                  <p className="text-xs text-[#8a7e70] mt-0.5">
                    Açıkken rol şablonu UYGULANMAZ; kullanıcının yetkisi tam olarak bu listedir
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="custom-perms-toggle"
                    checked={showCustomPerms}
                    onCheckedChange={toggleCustomPerms}
                  />
                  <Label htmlFor="custom-perms-toggle" className="text-sm cursor-pointer">
                    Özel yetkiler kullan
                  </Label>
                </div>
              </div>

              {showCustomPerms && (
                <div className="mt-3">
                  <p className="text-xs text-[#7a6a2e] bg-[#7a6a2e]/10 px-3 py-2 rounded-md mb-3">
                    Rol şablonu bu kullanıcıya <b>uygulanmaz</b>: yetkisi tam olarak aşağıdaki
                    listedir, işaretlenmeyen sayfa kapalıdır. <b>Dikkat:</b> kullanıcı rol
                    şablonundan kopar — şablona sonradan eklenen sayfalar bu kullanıcıya
                    ulaşmaz, yetkiyi elle güncellemeniz gerekir.
                  </p>
                  {kullaniciMatrisi}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEdit(false); setShowCustomPerms(false); }} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleUpdate} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Geçici Şifre</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              Bu şifreyi kullanıcıya iletin. İlk girişte şifre değiştirmesi istenecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center">
            <p className="font-mono text-2xl font-bold text-[#3d6e4e] bg-[#e8dfd0] px-4 py-3 rounded-lg">
              {tempPassword}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              toast.success('Şifre kopyalandı');
            }} variant="outline" className="border-[#d0c4b0]">
              Kopyala
            </Button>
            <Button onClick={() => setTempPassword(null)} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              Tamam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ─── ROLES TAB ───


// Matris BILESEN OLARAK DISARIDA tanimli. Ana bilesenin icinde tanimlaninca
// her render'da yeni bir bilesen turu oluyor; React tum agaci (15 satir x 5
// kutu = 75 Radix bileseni) sokup yeniden kuruyordu. Her tikta yasanan bu
// sokme-kurma isaretlemenin tutmamasina yol aciyordu.
function PermissionMatrix({ pages, actions, permissions, onToggle }) {
  return (
    <div className="border border-[#d0c4b0] rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#e8dfd0]">
            <TableHead className="text-[#1a1916] font-bold">Sayfa</TableHead>
            {actions.map(a => (
              <TableHead key={a} className="text-[#1a1916] font-bold text-center capitalize">{a}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pages.map(page => (
            <TableRow key={page.key} className="hover:bg-[#f5f0e8]">
              <TableCell className="font-medium">{page.label}</TableCell>
              {actions.map(action => {
                const isaretli = (permissions[page.key] || []).includes(action);
                return (
                  <TableCell key={action} className="text-center">
                    {/* Kutu <label> ile sarilmaz: HTML'de <button> etiketlenebilir
                        bir ogedir, tiklama iki kez tetiklenip isaretleme
                        aninda geri alinabilir. */}
                    <Checkbox
                      checked={isaretli}
                      onCheckedChange={() => onToggle(page.key, action)}
                      aria-label={`${page.label} ${action}`}
                      data-testid={`perm-${page.key}-${action}`}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RolesTab() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', description: '', permissions: {}
  });

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/roles/list');
      setRoles(res.data || []);
    } catch (err) {
      toast.error('Roller yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleSeedRoles = async () => {
    try {
      const res = await api.post('/users/roles/seed');
      toast.success(`${res.data.created} rol oluşturuldu`);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const togglePermission = (page, action) => {
    setForm(f => {
      const perms = { ...f.permissions };
      if (!perms[page]) perms[page] = [];
      
      if (perms[page].includes(action)) {
        perms[page] = perms[page].filter(a => a !== action);
      } else {
        perms[page] = [...perms[page], action];
      }
      
      if (perms[page].length === 0) delete perms[page];
      return { ...f, permissions: perms };
    });
  };

  const handleCreate = async () => {
    if (!form.code || !form.name) {
      toast.error('Kod ve ad zorunlu');
      return;
    }
    try {
      // api'nin taban adresi zaten /api/erp; basina '/erp' eklenince adres
      // /api/erp/erp/users/roles oluyor ve 404 donuyordu — rol hic olusmuyordu.
      await api.post('/users/roles', form);
      toast.success('Rol oluşturuldu');
      fetchRoles();
      setShowNew(false);
      setForm({ code: '', name: '', description: '', permissions: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.patch(`/users/roles/${editingRole.code}`, form);
      toast.success('Rol güncellendi');
      fetchRoles();
      setShowEdit(false);
      setEditingRole(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const handleDelete = async (code) => {
    if (!confirm('Bu rolü silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/users/roles/${code}`);
      toast.success('Rol silindi');
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({
      code: role.code,
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || {}
    });
    setShowEdit(true);
  };

  const matris = (
    <PermissionMatrix
      pages={PERMISSION_PAGES}
      actions={PERMISSION_ACTIONS}
      permissions={form.permissions}
      onToggle={togglePermission}
    />
  );

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Shield size={18} /> Rol Şablonları
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedRoles} className="border-[#d0c4b0]" data-testid="seed-roles-btn">
            <RefreshCw size={16} className="mr-2" /> Varsayılan Rolleri Oluştur
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="add-role-btn">
            <Plus size={16} className="mr-2" /> Yeni Rol
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold">Kod</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Açıklama</TableHead>
              <TableHead className="text-[#1a1916] font-bold">Tür</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map(r => (
              <TableRow key={r.code} className="hover:bg-[#f5f0e8]">
                <TableCell className="font-mono">{r.code}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{r.description || '-'}</TableCell>
                <TableCell>
                  {r.is_system ? (
                    <Badge className="bg-blue-100 text-blue-800">Sistem</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">Özel</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="text-[#8a7e70]">
                    <Pencil size={14} />
                  </Button>
                  {!r.is_system && silmeYetkisi && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(r.code)} className="text-red-600">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-[#8a7e70]">
                  Rol bulunamadı. "Varsayılan Rolleri Oluştur" butonuna tıklayın.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* New Role Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        {/* flex + min-h-0: yetki matrisi uzun oldugu icin govde kaysin,
            baslik ve kaydet butonu sabit kalsin. */}
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-[#1a1916] font-bold">Yeni Rol</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Kod *</Label>
                <Input 
                  value={form.code}
                  onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  className="bg-white border-[#d0c4b0]"
                  placeholder="ornek_rol"
                  data-testid="role-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Ad *</Label>
                <Input 
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-white border-[#d0c4b0]"
                  placeholder="Örnek Rol"
                  data-testid="role-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Açıklama</Label>
              <Input 
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
                placeholder="Rol açıklaması..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Yetkiler</Label>
              {matris}
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-[#d0c4b0] pt-4">
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleCreate} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="save-role-btn">
              Rol Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-[#1a1916] font-bold">Rol Düzenle: {editingRole?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Kod</Label>
                <Input 
                  value={form.code}
                  disabled={editingRole?.is_system}
                  className="bg-white border-[#d0c4b0]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Ad</Label>
                <Input 
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-white border-[#d0c4b0]"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Açıklama</Label>
              <Input 
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="bg-white border-[#d0c4b0]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Yetkiler</Label>
              {matris}
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-[#d0c4b0] pt-4">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={handleUpdate} className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]">
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── INTEGRATIONS TAB ───
function IntegrationsTab() {
  const [llmKey, setLlmKey] = useState('');
  const [keySet, setKeySet] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [geminiKey, setGeminiKey] = useState('');
  const [geminiKeySet, setGeminiKeySet] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [savingGemini, setSavingGemini] = useState(false);

  // SMTP — personel portalının giriş kodu buradan gidiyor. Şifre sunucudan HİÇ
  // dönmüyor (`sifre_var`); boş bırakılırsa kayıtlı şifreye dokunulmaz.
  const [smtp, setSmtp] = useState({ sunucu: '', port: 587, guvenlik: 'starttls',
    kullanici: '', sifre: '', gonderen: '', gonderen_adi: '' });
  const [smtpVar, setSmtpVar] = useState(false);
  const [smtpKaydediyor, setSmtpKaydediyor] = useState(false);
  const [smtpTest, setSmtpTest] = useState({ alici: '', mesgul: false, sonuc: null });

  useEffect(() => {
    api.get('/admin/settings').then(r => {
      setKeySet(r.data.llm_api_key_set || false);
      setGeminiKeySet(r.data.gemini_api_key_set || false);
      const m = r.data.smtp || {};
      setSmtpVar(Boolean(m.sifre_var));
      setSmtp(p => ({ ...p, ...m, sifre: '' }));
    }).catch(() => {});
  }, []);

  const smtpKaydet = async () => {
    if (!smtp.sunucu.trim() || !smtp.gonderen.trim()) {
      toast.error('Sunucu ve gönderen adresi zorunlu'); return;
    }
    setSmtpKaydediyor(true);
    try {
      const r = await api.patch('/admin/settings', {
        smtp: { ...smtp, port: Number(smtp.port) || 587 } });
      const m = r.data.smtp || {};
      setSmtpVar(Boolean(m.sifre_var));
      setSmtp(p => ({ ...p, ...m, sifre: '' }));
      toast.success('E-posta ayarları kaydedildi');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydetme hatası');
    } finally { setSmtpKaydediyor(false); }
  };

  const smtpDene = async () => {
    setSmtpTest(t => ({ ...t, mesgul: true, sonuc: null }));
    try {
      const r = await api.post('/admin/settings/test-eposta', { alici: smtpTest.alici });
      setSmtpTest(t => ({ ...t, mesgul: false, sonuc: { ok: true, metin: `${r.data.alici} adresine gönderildi` } }));
    } catch (e) {
      setSmtpTest(t => ({ ...t, mesgul: false,
        sonuc: { ok: false, metin: e?.response?.data?.detail || 'Gönderilemedi' } }));
    }
  };

  const save = async () => {
    if (!llmKey.trim()) return;
    setSaving(true);
    try {
      const r = await api.patch('/admin/settings', { llm_api_key: llmKey });
      setKeySet(r.data.llm_api_key_set || false);
      setLlmKey('');
      setTestResult(null);
      toast.success('API anahtarı kaydedildi');
    } catch {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('API anahtarını silmek istiyor musunuz?')) return;
    await api.patch('/admin/settings', { llm_api_key: null });
    setKeySet(false);
    setTestResult(null);
    toast.success('API anahtarı silindi');
  };

  const saveGemini = async () => {
    if (!geminiKey.trim()) return;
    setSavingGemini(true);
    try {
      const r = await api.patch('/admin/settings', { gemini_api_key: geminiKey });
      setGeminiKeySet(r.data.gemini_api_key_set || false);
      setGeminiKey('');
      toast.success('Gemini API anahtarı kaydedildi');
    } catch {
      toast.error('Kaydetme hatası');
    } finally {
      setSavingGemini(false);
    }
  };

  const removeGemini = async () => {
    if (!window.confirm('Gemini API anahtarını silmek istiyor musunuz?')) return;
    await api.patch('/admin/settings', { gemini_api_key: null });
    setGeminiKeySet(false);
    toast.success('Gemini API anahtarı silindi');
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/admin/settings/test-llm');
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ connection: false, error: e.response?.data?.detail || 'Bağlantı hatası' });
    } finally {
      setTesting(false);
    }
  };

  const fmt = (v) => v != null ? `$${Number(v).toFixed(2)}` : '—';

  const smtpKarti = (
    <Card className="bg-[#faf7f2] border-[#d0c4b0] mb-6">
      <CardHeader className="border-b border-[#d0c4b0]">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Send size={18} /> E-posta Gönderimi (SMTP)
        </CardTitle>
        <p className="text-xs text-[#8a7e70]">
          Personel ekranının (<code>/portal</code>) tek kullanımlık giriş kodu buradan gönderilir.
          Ayar yapılmazsa personel giriş yapamaz. Şifre kaydedildikten sonra bir daha gösterilmez;
          alanı boş bırakırsanız kayıtlı şifre korunur.
        </p>
        <p className="text-xs text-[#5c554b] bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 rounded-md px-3 py-2 mt-2">
          <b>Gmail / Google Workspace:</b> sunucu <code>smtp.gmail.com</code>, port <b>587</b> (STARTTLS)
          ya da <b>465</b> (SSL/TLS). Gmail <b>normal hesap şifresini kabul etmez</b> — hesapta
          2&nbsp;Adımlı Doğrulama'yı açıp <b>16 haneli Uygulama Şifresi</b> (App Password) üretin ve
          buraya onu yazın. <b>Gönderen adres</b>, giriş yapılan hesabın kendisi olmalı; farklı bir
          adres kullanacaksanız o adresin Gmail'de "Send mail as" ile doğrulanmış olması gerekir.
        </p>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-[#3F3E39]">Sunucu *</Label>
            <Input value={smtp.sunucu} placeholder="smtp.ornek.com" data-testid="smtp-sunucu"
              onChange={(e) => setSmtp(p => ({ ...p, sunucu: e.target.value }))}
              className="bg-white border-[#d0c4b0]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[#3F3E39]">Port</Label>
              <Input type="number" value={smtp.port} data-testid="smtp-port"
                onChange={(e) => {
                  // Port ile guvenlik BIRLIKTE degisir: 465+STARTTLS denemesi
                  // "WRONG_VERSION_NUMBER" veriyor ve sebebi anlasilmiyor
                  const v = e.target.value;
                  setSmtp(p => ({ ...p, port: v,
                    guvenlik: String(v) === '465' ? 'ssl' : String(v) === '587' ? 'starttls' : p.guvenlik }));
                }}
                className="bg-white border-[#d0c4b0]" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#3F3E39]">Güvenlik</Label>
              <select value={smtp.guvenlik} data-testid="smtp-guvenlik"
                onChange={(e) => {
                  const g = e.target.value;
                  setSmtp(p => ({ ...p, guvenlik: g,
                    port: g === 'ssl' ? 465 : g === 'starttls' ? 587 : p.port }));
                }}
                className="w-full h-10 rounded-md border border-[#d0c4b0] bg-white px-2 text-sm">
                <option value="starttls">STARTTLS (587)</option>
                <option value="ssl">SSL/TLS (465)</option>
                <option value="yok">Yok</option>
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[#3F3E39]">Kullanıcı adı</Label>
            <Input value={smtp.kullanici} autoComplete="off" data-testid="smtp-kullanici"
              onChange={(e) => setSmtp(p => ({ ...p, kullanici: e.target.value }))}
              className="bg-white border-[#d0c4b0]" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#3F3E39]">
              Şifre {smtpVar && <span className="text-xs text-[#3d6e4e]">(kayıtlı)</span>}
            </Label>
            <Input type="password" value={smtp.sifre} autoComplete="new-password"
              placeholder={smtpVar ? '•••••• (değiştirmek için yazın)' : ''} data-testid="smtp-sifre"
              onChange={(e) => setSmtp(p => ({ ...p, sifre: e.target.value }))}
              className="bg-white border-[#d0c4b0]" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#3F3E39]">Gönderen adres *</Label>
            <Input value={smtp.gonderen} placeholder="ik@ornek.com" data-testid="smtp-gonderen"
              onChange={(e) => setSmtp(p => ({ ...p, gonderen: e.target.value }))}
              className="bg-white border-[#d0c4b0]" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#3F3E39]">Gönderen adı</Label>
            <Input value={smtp.gonderen_adi} placeholder="Toros ERP" data-testid="smtp-gonderen-adi"
              onChange={(e) => setSmtp(p => ({ ...p, gonderen_adi: e.target.value }))}
              className="bg-white border-[#d0c4b0]" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button onClick={smtpKaydet} disabled={smtpKaydediyor} data-testid="smtp-kaydet"
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
            <Save size={15} className="mr-1.5" /> Kaydet
          </Button>
          <Input value={smtpTest.alici} placeholder="deneme@ornek.com" data-testid="smtp-test-alici"
            onChange={(e) => setSmtpTest(t => ({ ...t, alici: e.target.value }))}
            className="bg-white border-[#d0c4b0] w-56" />
          <Button variant="outline" onClick={smtpDene} disabled={smtpTest.mesgul}
            className="border-[#d0c4b0]" data-testid="smtp-test">
            {smtpTest.mesgul ? 'Gönderiliyor…' : 'Test e-postası gönder'}
          </Button>
          {smtpTest.sonuc && (
            <span className={`text-sm ${smtpTest.sonuc.ok ? 'text-[#3d6e4e]' : 'text-[#a34b3f]'}`}
              data-testid="smtp-test-sonuc">
              {smtpTest.sonuc.ok ? '✓ ' : '✗ '}{smtpTest.sonuc.metin}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      {smtpKarti}
      <WhatsAppAyarKarti />
    <Card className="border-[#d0c4b0]">
      <CardHeader>
        <CardTitle className="text-[#1a1916] flex items-center gap-2">
          <Key size={18} /> Entegrasyon Ayarları
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label className="text-[#1a1916] font-semibold">AI Fatura Tanıma — OpenAI API Anahtarı</Label>
          <p className="text-sm text-[#8a7e70]">
            Fatura görselini yükleyip GPT-4o ile ürünleri otomatik tanımak için OpenAI API anahtarınızı girin.
          </p>
          {keySet && (
            <div className="flex items-center gap-2 p-3 bg-[#3d6e4e]/10 border border-[#3d6e4e]/30 rounded-lg">
              <Check size={16} className="text-[#3d6e4e]" />
              <span className="text-sm text-[#3d6e4e] font-medium">API anahtarı yapılandırılmış</span>
              <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove}>
                <Trash2 size={14} className="mr-1" /> Sil
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder={keySet ? 'Yeni anahtar girerek güncelleyin...' : 'API anahtarını girin...'}
                value={llmKey}
                onChange={e => setLlmKey(e.target.value)}
                className="bg-white border-[#d0c4b0] pr-10"
              />
              <button type="button" onClick={() => setShowKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]">
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <Button onClick={save} disabled={saving || !llmKey.trim()}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
              {saving ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
              Kaydet
            </Button>
          </div>

          {keySet && (
            <div className="pt-1">
              <Button variant="outline" onClick={testConnection} disabled={testing}
                className="border-[#d0c4b0] text-[#1a1916] hover:bg-[#f5f0e8]">
                {testing ? <RefreshCw size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
                Bağlantıyı Test Et
              </Button>
            </div>
          )}

          {testResult && (
            <div className={`mt-3 p-4 rounded-lg border space-y-3 ${testResult.connection ? 'bg-[#3d6e4e]/10 border-[#3d6e4e]/30' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2">
                {testResult.connection
                  ? <Check size={16} className="text-[#3d6e4e]" />
                  : <X size={16} className="text-red-600" />}
                <span className={`text-sm font-semibold ${testResult.connection ? 'text-[#3d6e4e]' : 'text-red-600'}`}>
                  {testResult.connection ? `Bağlantı başarılı — ${testResult.model || 'gpt-4o-mini'}` : `Bağlantı hatası: ${testResult.error}`}
                </span>
              </div>

              {testResult.connection && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  {testResult.billing && (
                    <>
                      <div className="bg-white/70 rounded-lg p-3 border border-[#d0c4b0]">
                        <p className="text-xs text-[#8a7e70] mb-1">Plan</p>
                        <p className="text-sm font-semibold text-[#1a1916]">{testResult.billing.plan || '—'}</p>
                      </div>
                      <div className="bg-white/70 rounded-lg p-3 border border-[#d0c4b0]">
                        <p className="text-xs text-[#8a7e70] mb-1">Aylık Limit</p>
                        <p className="text-sm font-semibold text-[#1a1916]">{fmt(testResult.billing.hard_limit_usd)}</p>
                      </div>
                      <div className="bg-white/70 rounded-lg p-3 border border-[#d0c4b0]">
                        <p className="text-xs text-[#8a7e70] mb-1">Soft Limit</p>
                        <p className="text-sm font-semibold text-[#1a1916]">{fmt(testResult.billing.soft_limit_usd)}</p>
                      </div>
                    </>
                  )}
                  {testResult.usage_today && (
                    <div className="bg-white/70 rounded-lg p-3 border border-[#d0c4b0]">
                      <p className="text-xs text-[#8a7e70] mb-1">Bu Ay Harcanan</p>
                      <p className="text-sm font-semibold text-[#1a1916]">{fmt(testResult.usage_today.total_usage_usd)}</p>
                    </div>
                  )}
                  {!testResult.billing && !testResult.usage_today && (
                    <p className="text-xs text-[#8a7e70] col-span-2">Kullanım bilgisi bu hesap türü için mevcut değil.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[#d0c4b0] pt-6 space-y-3">
          <Label className="text-[#1a1916] font-semibold">AI Fatura Tanıma — Gemini API Anahtarı</Label>
          <p className="text-sm text-[#8a7e70]">
            Fatura görsellerini okumak için Google Gemini 2.0 Flash kullanılır. <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="underline hover:text-[#1a1916]">aistudio.google.com</a> adresinden ücretsiz key alabilirsiniz.
          </p>
          {geminiKeySet && (
            <div className="flex items-center gap-2 p-3 bg-[#3d6e4e]/10 border border-[#3d6e4e]/30 rounded-lg">
              <Check size={16} className="text-[#3d6e4e]" />
              <span className="text-sm text-[#3d6e4e] font-medium">Gemini API anahtarı yapılandırılmış</span>
              <Button variant="ghost" size="sm" className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50" onClick={removeGemini}>
                <Trash2 size={14} className="mr-1" /> Sil
              </Button>
            </div>
          )}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={showGeminiKey ? 'text' : 'password'}
                placeholder={geminiKeySet ? 'Yeni anahtar girerek güncelleyin...' : 'AIza... şeklinde Gemini API anahtarı girin'}
                value={geminiKey}
                onChange={e => setGeminiKey(e.target.value)}
                className="bg-white border-[#d0c4b0] pr-10"
              />
              <button type="button" onClick={() => setShowGeminiKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a7e70] hover:text-[#1a1916]">
                {showGeminiKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <Button onClick={saveGemini} disabled={savingGemini || !geminiKey.trim()}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
              {savingGemini ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
              Kaydet
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
    </>
  );
}

// ─── MCP ERİŞİM ANAHTARLARI ───
// Claude Desktop gibi yapay zekâ istemcilerinin sisteme kalıcı bağlanması için.
// Anahtar bir KULLANICIYA bağlıdır: asistan o kullanıcının yetkileriyle sınırlıdır.
function McpKeysTab() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [name, setName] = useState('Claude Desktop');
  const [creating, setCreating] = useState(false);
  const [yeni, setYeni] = useState(null);   // yalnızca oluşturma anında dolu

  // Kurulum adimlari isletim sistemine gore degisiyor: yapilandirma dosyasinin
  // yeri, Python komutunun adi (python / python3) ve dosya yolu bicimi.
  // Tarayicidan tahmin ediyoruz ama kullanici degistirebiliyor — anahtari
  // baska bir bilgisayar icin uretiyor olabilir.
  const macTahmini = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad/.test(navigator.userAgent || '');
  const [isletim, setIsletim] = useState(macTahmini ? 'mac' : 'windows');
  const mac = isletim === 'mac';

  const varsayilanYol = mac
    ? '/Users/KULLANICI_ADI/toros_mcp_bridge.py'
    : 'C:\\toros-erp\\toros_mcp_bridge.py';
  const [kopruYolu, setKopruYolu] = useState(varsayilanYol);
  // Isletim sistemi degistiginde, kullanici yolu elle degistirmediyse guncelle
  useEffect(() => { setKopruYolu(varsayilanYol); }, [varsayilanYol]);

  const configDosyasi = mac
    ? '~/Library/Application Support/Claude/claude_desktop_config.json'
    : '%APPDATA%\\Claude\\claude_desktop_config.json';

  // TAM adres olmali. Uygulama kendi icinde goreli adres kullaniyor
  // (REACT_APP_BACKEND_URL bos), ama bu adres BASKA bir bilgisayardaki
  // koprüye gidiyor; "/mcp/" oraya yazilirsa istemci "Invalid URL" ile
  // aciliyor. Ortam degiskeni bossa tarayicinin adresinden turetiyoruz.
  const sunucuKoku = (process.env.REACT_APP_BACKEND_URL || window.location.origin)
    .replace(/\/+$/, '');
  const mcpUrl = `${sunucuKoku}/mcp/`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/mcp-keys');
      setKeys(r.data || []);
    } catch {
      toast.error('Anahtarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const olustur = async () => {
    setCreating(true);
    try {
      const r = await api.post('/mcp-keys', { name: name.trim() || 'Claude Desktop' });
      setYeni(r.data);
      setDialog(false);
      setName('Claude Desktop');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anahtar oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const iptal = async (k) => {
    if (!window.confirm(`"${k.name}" anahtarı iptal edilsin mi?\n\nBu anahtarla kurulmuş bağlantılar anında kesilir.`)) return;
    try {
      await api.delete(`/mcp-keys/${k.id}`);
      toast.success('Anahtar iptal edildi');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İptal edilemedi');
    }
  };

  const kopyala = async (metin, etiket) => {
    try {
      await navigator.clipboard.writeText(metin);
      toast.success(`${etiket} kopyalandı`);
    } catch {
      toast.error('Kopyalanamadı — metni elle seçip kopyalayın');
    }
  };

  // Claude Desktop yerel sureclerle stdio konusur, MCP sunucumuz HTTP konusur.
  // Aradaki koprüyu biz saglıyoruz (Python) — yaygın alternatif `npx mcp-remote`
  // Node.js gerektiriyor ve kurulu degilse surec hic baslamiyor.
  const config = (anahtar) => JSON.stringify({
    mcpServers: {
      'toros-erp': {
        // macOS'ta tam yol veriyoruz: Claude Desktop surecleri kullanicinin
        // kabuk PATH'i olmadan baslatiyor, ciplak "python3" bulunamayabiliyor.
        command: mac ? '/usr/bin/python3' : 'python',
        args: [kopruYolu, mcpUrl],
        env: { TOROS_MCP_KEY: anahtar },
      },
    },
  }, null, 2);

  // Tek komutluk kurulum: koprüyu indirir, yapilandirmayi kendisi yazar
  // (mevcut sunuculara dokunmadan, yedek alarak) ve baglantiyi sinar.
  // Elle kurulumda yol / adres / Python komutu / JSON soz dizimi olmak uzere
  // dort ayri yerde hata yapilabiliyordu.
  const indirmeUcu = `${sunucuKoku}/api/erp/mcp-keys/bridge`;

  const kurulumKomutu = (anahtar) => {
    if (mac) {
      return [
        `curl -fsSL -H "Authorization: Bearer ${anahtar}" \\`,
        `  "${indirmeUcu}" -o ~/toros_mcp_bridge.py \\`,
        `  && TOROS_MCP_KEY="${anahtar}" python3 ~/toros_mcp_bridge.py --kur "${mcpUrl}"`,
      ].join('\n');
    }
    return [
      `curl -fsSL -H "Authorization: Bearer ${anahtar}" ^`,
      `  "${indirmeUcu}" -o "%USERPROFILE%\\toros_mcp_bridge.py" ^`,
      `  && set "TOROS_MCP_KEY=${anahtar}" ^`,
      `  && python "%USERPROFILE%\\toros_mcp_bridge.py" --kur "${mcpUrl}"`,
    ].join('\n');
  };

  // Denetim komutu once koprüyu TAZELER: kullanicinin elindeki dosya eski
  // surum olabilir ve yeni secenekleri tanimaz. Anahtari yazdirmaya gerek
  // yok — mevcut yapilandirmadan okunuyor.
  const denetimKomutu = mac
    ? [
        `KEY=$(python3 -c "import json,os;print(json.load(open(os.path.expanduser('${configDosyasi}')))['mcpServers']['toros-erp']['env']['TOROS_MCP_KEY'])") \\`,
        `  && curl -fsSL -H "Authorization: Bearer $KEY" "${indirmeUcu}" -o ~/toros_mcp_bridge.py \\`,
        `  && python3 ~/toros_mcp_bridge.py --dogrula`,
      ].join('\n')
    : [
        `$k = (Get-Content "$env:APPDATA\\Claude\\claude_desktop_config.json" -Raw | ConvertFrom-Json).mcpServers.'toros-erp'.env.TOROS_MCP_KEY`,
        `curl.exe -fsSL -H "Authorization: Bearer $k" "${indirmeUcu}" -o "$env:USERPROFILE\\toros_mcp_bridge.py"`,
        `python "$env:USERPROFILE\\toros_mcp_bridge.py" --dogrula`,
      ].join('\n');

  const kopruIndir = async () => {
    try {
      const res = await api.get('/mcp-keys/bridge', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'toros_mcp_bridge.py'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Köprü indirildi');
    } catch {
      toast.error('Köprü indirilemedi');
    }
  };

  const tarih = (v) => v ? new Date(v).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  return (
    <div className="space-y-6">
      <Card className="border-[#d0c4b0]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-[#1a1916] flex items-center gap-2">
            <Bot size={18} /> Yapay Zekâ Bağlantısı (MCP)
          </CardTitle>
          <Button onClick={() => setDialog(true)}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
            <Plus size={14} className="mr-1" /> Yeni Anahtar
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#8a7e70]">
            Claude Desktop gibi bir yapay zekâ istemcisinin sistemdeki verilerle analiz yapabilmesi için
            erişim anahtarı oluşturun. Anahtar <strong>sizin kullanıcınıza</strong> bağlıdır; asistan yalnızca
            sizin yetkiniz dahilindeki verileri görebilir. Anahtarı istediğiniz an iptal edebilirsiniz.
          </p>

          <div className="rounded-lg border border-[#d0c4b0] bg-[#faf8f4] p-3 text-sm">
            <span className="text-[#8a7e70]">Sunucu adresi: </span>
            <code className="font-mono text-[#1a1916]">{mcpUrl}</code>
            <Button variant="ghost" size="sm" className="ml-2 h-6 px-2" onClick={() => kopyala(mcpUrl, 'Adres')}>
              <Copy size={12} />
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-[#8a7e70]">Yükleniyor…</p>
          ) : keys.length === 0 ? (
            <p className="text-sm text-[#8a7e70] py-4 text-center">Henüz anahtar oluşturulmamış.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad</TableHead>
                  <TableHead>Anahtar</TableHead>
                  <TableHead>Oluşturulma</TableHead>
                  <TableHead>Son Kullanım</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map(k => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium text-[#1a1916]">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs text-[#8a7e70]">{k.key_prefix}…</TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">{tarih(k.created_at)}</TableCell>
                    <TableCell className="text-sm text-[#8a7e70]">{tarih(k.last_used_at)}</TableCell>
                    <TableCell>
                      {k.revoked_at
                        ? <Badge variant="outline" className="text-red-600 border-red-300">İptal edildi</Badge>
                        : <Badge className="bg-[#3d6e4e] text-white hover:bg-[#3d6e4e]">Aktif</Badge>}
                    </TableCell>
                    <TableCell>
                      {!k.revoked_at && silmeYetkisi && (
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => iptal(k)}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Yeni anahtar adı */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni MCP Anahtarı</DialogTitle>
            <DialogDescription>
              Anahtarı hangi cihaz veya uygulama için oluşturduğunuzu yazın. Daha sonra tanımanıza yardımcı olur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Anahtar Adı</Label>
            <Input value={name} onChange={e => setName(e.target.value)} maxLength={60}
              className="bg-white border-[#d0c4b0]" placeholder="Örn. Ofis bilgisayarı — Claude Desktop" />
            <p className="text-xs text-[#8a7e70]">
              Anahtar <strong>{user?.email}</strong> kullanıcısına bağlanacak.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Vazgeç</Button>
            <Button onClick={olustur} disabled={creating}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
              {creating ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Oluşturulan anahtar — BİR KEZ gösterilir */}
      <Dialog open={!!yeni} onOpenChange={(o) => { if (!o) setYeni(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Anahtar oluşturuldu</DialogTitle>
            <DialogDescription>
              Kurulumu tamamlayın — bu pencereyi kapattığınızda anahtar bir daha gösterilmeyecek.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <span className="text-sm text-amber-900">
                Anahtar sistemde saklanmıyor (yalnızca doğrulama özeti tutuluyor).
                Kaybederseniz yenisini oluşturmanız gerekir.
              </span>
            </div>

            <div className="space-y-1">
              <Label className="text-[#1a1916] font-semibold">Anahtar</Label>
              <div className="flex gap-2">
                <code className="flex-1 min-w-0 font-mono text-xs bg-[#faf8f4] border border-[#d0c4b0] rounded-lg p-3 break-all">
                  {yeni?.key}
                </code>
                <Button variant="outline" onClick={() => kopyala(yeni.key, 'Anahtar')}>
                  <Copy size={14} />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1a1916] font-semibold">Claude Desktop hangi bilgisayarda?</Label>
              <div className="flex gap-2">
                {[['windows', 'Windows'], ['mac', 'macOS']].map(([deger, etiket]) => (
                  <Button key={deger} type="button" size="sm"
                    variant={isletim === deger ? 'default' : 'outline'}
                    onClick={() => setIsletim(deger)}
                    className={isletim === deger
                      ? 'bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]'
                      : 'border-[#d0c4b0] text-[#3F3E39]'}>
                    {etiket}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-[#8a7e70]">
                Bağlantıyı kuracağınız bilgisayarı seçin — sunucu değil. Aşağıdaki
                adımlar ve yapılandırma buna göre değişir.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1a1916] font-semibold">Kurulum — tek komut</Label>
              <p className="text-sm text-[#8a7e70]">
                Aşağıdaki komutu {mac ? "Mac'inizde Terminal'e" : 'Komut İstemi\'ne'} yapıştırıp
                çalıştırın. Köprüyü indirir, yapılandırmayı kendisi yazar (varsa mevcut
                sunuculara dokunmadan, yedek alarak) ve bağlantıyı sınayıp sonucu söyler.
              </p>
              {/* min-w-0: flex ogesi min-width:auto ile iceriginden dar olamaz,
                  bu yuzden uzun komut modalin disina tasiyordu. */}
              <div className="flex gap-2 items-start">
                <pre className="flex-1 min-w-0 font-mono text-xs bg-[#1a1916] text-[#f5f0e8]
                                rounded-lg p-3 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                  {yeni ? kurulumKomutu(yeni.key) : ''}
                </pre>
                <Button variant="outline" className="shrink-0"
                  onClick={() => kopyala(kurulumKomutu(yeni.key), 'Komut')}>
                  <Copy size={14} className="mr-1.5" /> Kopyala
                </Button>
              </div>
              <p className="text-xs text-[#8a7e70]">
                "BASARILI" yazdığını gördükten sonra Claude Desktop'ı tamamen kapatıp açın
                {mac && ' (Cmd+Q — pencereyi kapatmak yetmez)'}.
              </p>

              <div className="pt-2 space-y-1">
                <Label className="text-xs text-[#3F3E39] font-semibold">
                  Sorun yaşarsanız — denetim komutu
                </Label>
                <p className="text-xs text-[#8a7e70]">
                  Claude "bağlantım yok" diyorsa bunu çalıştırın; yapılandırmayı,
                  dosya yollarını, anahtarı ve sunucudaki araçları tek tek denetler.
                </p>
                <div className="flex gap-2 items-start">
                  <pre className="flex-1 min-w-0 font-mono text-xs bg-[#faf8f4] border border-[#d0c4b0]
                                  rounded-lg p-2.5 whitespace-pre-wrap break-all">{denetimKomutu}</pre>
                  <Button variant="outline" size="sm" className="shrink-0"
                    onClick={() => kopyala(denetimKomutu, 'Denetim komutu')}>
                    <Copy size={13} />
                  </Button>
                </div>
              </div>
            </div>

            <details className="rounded-lg border border-[#d0c4b0] bg-[#faf8f4] p-3">
              <summary className="text-sm font-semibold text-[#1a1916] cursor-pointer">
                Elle kurmak isterseniz
              </summary>
              <div className="space-y-3 pt-3">
                <div className="space-y-2">
                  <Label className="text-xs text-[#3F3E39] font-semibold">1. Köprüyü indirin</Label>
                  <Button variant="outline" size="sm" onClick={kopruIndir} className="border-[#d0c4b0]">
                    <Download size={13} className="mr-1.5" /> toros_mcp_bridge.py indir
                  </Button>
                  <div>
                    <Label className="text-xs text-[#8a7e70]">Dosyayı kaydettiğiniz tam yol</Label>
                    <Input value={kopruYolu} onChange={e => setKopruYolu(e.target.value)}
                      className="bg-white border-[#d0c4b0] font-mono text-xs mt-1"
                      placeholder={varsayilanYol} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#3F3E39] font-semibold">
                    2. Şunu <code className="font-mono">{configDosyasi}</code> dosyasına yazın
                  </Label>
                  <div className="flex gap-2">
                    <pre className="flex-1 min-w-0 font-mono text-xs bg-white border border-[#d0c4b0] rounded-lg p-3 overflow-x-auto">
                      {yeni ? config(yeni.key) : ''}
                    </pre>
                    <Button variant="outline" size="sm" onClick={() => kopyala(config(yeni.key), 'Yapılandırma')}>
                      <Copy size={13} />
                    </Button>
                  </div>
                  <p className="text-xs text-[#8a7e70]">
                    Dosyada başka sunucular varsa yalnızca <code className="font-mono">toros-erp</code> bölümünü ekleyin.
                    {mac && ' Farklı bir Python kullanıyorsanız "command" değerini "which python3" çıktısıyla değiştirin.'}
                  </p>
                </div>
              </div>
            </details>
          </div>

          <DialogFooter>
            <Button onClick={() => setYeni(null)}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
              Kaydettim, kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── PUANTAJ AYARLARI ───
// Katsayilar app_settings.puantaj altinda tutulur; puantaj ekrani ve
// sunucudaki maas hesabi ayni degeri okur.
const PUANTAJ_ALANLARI = [
  {
    anahtar: 'resmi_tatil_katsayi', etiket: 'Resmi Tatil Çalışması Katsayısı',
    varsayilan: 2, adim: '0.1',
    aciklama: 'RTÇ işaretlenen gün kaç gün sayılsın. 2 girilirse çalışan o gün için '
      + 'bir yevmiye fazladan alır.',
  },
  {
    anahtar: 'mesai_katsayi', etiket: 'Fazla Mesai Katsayısı',
    varsayilan: 1.5, adim: '0.1',
    aciklama: 'Saatlik ücret bu katsayı ile çarpılır. Yasal fazla çalışma zammı 1,5 katıdır.',
  },
  {
    anahtar: 'gunluk_calisma_saati', etiket: 'Günlük Normal Çalışma Saati',
    varsayilan: 7.5, adim: '0.5',
    aciklama: 'Saatlik ücret hesabında kullanılır: günlük ücret ÷ bu saat.',
  },
];

function PuantajAyarlariTab() {
  const [deger, setDeger] = useState({});
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    api.get('/admin/settings')
      .then(r => setDeger(r.data?.puantaj || {}))
      .catch(() => toast.error('Ayarlar yüklenemedi'))
      .finally(() => setYukleniyor(false));
  }, []);

  const oku = (a) => {
    const v = deger[a.anahtar];
    return v === undefined || v === null || v === '' ? a.varsayilan : v;
  };

  const kaydet = async () => {
    const gonder = {};
    for (const a of PUANTAJ_ALANLARI) {
      const v = parseFloat(String(oku(a)).replace(',', '.'));
      const enAz = a.enAz ?? Number.MIN_VALUE;
      if (!(v >= enAz)) {
        toast.error(a.enAz === 0
          ? `${a.etiket} negatif olamaz`
          : `${a.etiket} sıfırdan büyük olmalı`);
        return;
      }
      gonder[a.anahtar] = v;
    }
    gonder.rapor_ucretli = raporUcretli();
    setKaydediliyor(true);
    try {
      await api.patch('/admin/settings', { puantaj: gonder });
      setDeger(gonder);
      toast.success('Puantaj ayarları kaydedildi');
    } catch {
      toast.error('Kaydetme hatası');
    } finally {
      setKaydediliyor(false);
    }
  };

  // Varsayılan ÜCRETLİ (sunucudaki PUANTAJ_BOOL ile aynı): rapor durumu
  // eklenmeden önce raporlu günler maaştan kesilmiyordu.
  const raporUcretli = () => deger.rapor_ucretli !== false;

  const ornek = () => {
    const gunluk = 1000;
    const saatlik = gunluk / (parseFloat(oku(PUANTAJ_ALANLARI[2])) || 7.5);
    const tatil = gunluk * ((parseFloat(oku(PUANTAJ_ALANLARI[0])) || 2) - 1);
    const mesai = saatlik * (parseFloat(oku(PUANTAJ_ALANLARI[1])) || 1.5);
    const gunSaati = parseFloat(oku(PUANTAJ_ALANLARI[2])) || 7.5;
    const tatilKatsayi = parseFloat(oku(PUANTAJ_ALANLARI[0])) || 2;
    const mesaiKatsayi = parseFloat(oku(PUANTAJ_ALANLARI[1])) || 1.5;
    const bicim = (n) => n.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
    return {
      saatlik: bicim(saatlik), tatil: bicim(tatil), mesai: bicim(mesai),
      tatilSaat: bicim(Math.max(0, tatilKatsayi - 1) * gunSaati),
      mesaiSaat: bicim(mesaiKatsayi),
    };
  };

  if (yukleniyor) return <p className="text-sm text-[#8a7e70]">Yükleniyor…</p>;
  const o = ornek();

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl" data-testid="puantaj-ayarlari">
      <CardHeader>
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <CalendarDays size={18} /> Puantaj Ayarları
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {PUANTAJ_ALANLARI.map(a => (
          <div key={a.anahtar}>
            <Label className="text-[#1a1916] font-medium">{a.etiket}</Label>
            <Input
              type="number" step={a.adim} min="0"
              value={oku(a)}
              onChange={(e) => setDeger(p => ({ ...p, [a.anahtar]: e.target.value }))}
              className="w-40 mt-1 bg-white border-[#d0c4b0]"
              data-testid={`puantaj-ayar-${a.anahtar}`}
            />
            <p className="text-xs text-[#8a7e70] mt-1">{a.aciklama}</p>
          </div>
        ))}


        <div className="border-t border-[#d0c4b0] pt-4">
          <Label className="text-[#1a1916] font-medium">Raporlu Günler</Label>
          <p className="text-xs text-[#8a7e70] mt-1 mb-2">
            Puantajda "Rapor" işaretli günler maaştan kesilsin mi? SGK geçici iş göremezlik
            ödeneğini üçüncü günden itibaren öder; işverenin raporlu günleri ödeyip
            ödememesi şirket politikasıdır.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            {[
              { deger: true, etiket: 'Ücretli — kesinti yok',
                aciklama: 'Raporlu gün ücretli izin gibi sayılır, aylık maaş değişmez.' },
              { deger: false, etiket: 'Ücretsiz — kesilsin',
                aciklama: 'Raporlu gün ücretsiz izin gibi sayılır, günlük ücret düşülür.' },
            ].map(k => (
              <button key={String(k.deger)} type="button"
                onClick={() => setDeger(p => ({ ...p, rapor_ucretli: k.deger }))}
                className={`flex-1 text-left rounded-lg border p-3 transition-colors
                  ${raporUcretli() === k.deger
                    ? 'border-[#3d6e4e] bg-[#3d6e4e]/10'
                    : 'border-[#d0c4b0] bg-white hover:bg-[#f5f0e8]'}`}
                data-testid={`puantaj-rapor-${k.deger ? 'ucretli' : 'ucretsiz'}`}>
                <p className="text-sm font-semibold text-[#1a1916]">{k.etiket}</p>
                <p className="text-xs text-[#8a7e70] mt-0.5">{k.aciklama}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-[#d0c4b0] bg-[#f5f0e8] p-3">
          <p className="text-xs font-semibold text-[#8a7e70] uppercase tracking-wide mb-2">
            Günlük ücreti 1.000 TL olan personel için
          </p>
          <ul className="text-sm text-[#1a1916] space-y-1">
            <li>Saatlik ücret: <b>{o.saatlik} TL</b></li>
            <li>Resmi tatil çalışması, o gün için ek: <b>{o.tatil} TL</b></li>
            <li>1 saat fazla mesai: <b>{o.mesai} TL</b></li>
            <li className="text-[#8a7e70]">
              İzne yazılırsa: resmi tatil çalışması <b>{o.tatilSaat} saat</b>,
              1 saat fazla mesai <b>{o.mesaiSaat} saat</b> denkleştirme izni.
            </li>
          </ul>
        </div>

        <p className="text-xs text-[#8a7e70]">
          Değişiklik yalnızca <b>henüz onaylanmamış</b> puantajları etkiler.
          Onaylanan aylar tahakkuk anındaki tutarla kalır. Varsayılan karşılık da
          böyledir: değiştirirseniz, günde açıkça işaretlenmemiş ve henüz
          onaylanmamış günler yeni varsayılana göre hesaplanır.
        </p>

        <Button onClick={kaydet} disabled={kaydediliyor}
          className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]" data-testid="puantaj-ayar-kaydet">
          <Save size={16} className="mr-2" /> {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── SORUMLULUK MERKEZLERİ ───
//
// `company_id` tüzel kişilik sınırı; sorumluluk merkezi onun altındaki iş/proje
// sınırı ve kârlılık ONUN bazında ölçülüyor. Her merkezin bir ANA PARA BİRİMİ
// var: TL harcamalar rapor anında, harcamanın gerçekleştiği günün TCMB kuruyla
// o para birimine çevriliyor (kur kayda yazılmıyor).
//
// Merkez SİLİNMEZ, pasife alınır — geçmiş işlemler merkeze bağlı kalmalı.
function SorumlulukMerkeziTab() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('admin', 'delete');
  const [merkezler, setMerkezler] = useState([]);
  const [hepsi, setHepsi] = useState(false);
  const [dialog, setDialog] = useState(null); // null | {} (yeni) | merkez (düzenle)
  const [kaydediyor, setKaydediyor] = useState(false);
  const [kurOnizleme, setKurOnizleme] = useState(null);

  const bos = { name: '', kod: '', para_birimi: 'TRY', type: 'profit_center', aciklama: '' };
  const [form, setForm] = useState(bos);

  const yukle = useCallback(() => {
    api.get('/sorumluluk-merkezleri', { params: { hepsi } })
      .then(r => setMerkezler(r.data || []))
      .catch(() => toast.error('Sorumluluk merkezleri okunamadı'));
  }, [hepsi]);
  useEffect(() => { yukle(); }, [yukle]);

  // Para birimi seçilince "bugün 1.000 TL kaç eder" göstermek, kurun CANLI
  // geldiğini ve hangi günün bülteninin kullanıldığını kullanıcıya kanıtlıyor.
  useEffect(() => {
    if (!dialog || !form.para_birimi || form.para_birimi === 'TRY') { setKurOnizleme(null); return; }
    let iptal = false;
    api.get('/sorumluluk-merkezleri/kur-onizleme', { params: { para_birimi: form.para_birimi, tutar: 1000 } })
      .then(r => { if (!iptal) setKurOnizleme(r.data); })
      .catch(() => { if (!iptal) setKurOnizleme(null); });
    return () => { iptal = true; };
  }, [dialog, form.para_birimi]);

  const ac = (m) => { setForm(m ? { ...bos, ...m } : bos); setDialog(m || {}); };

  const kaydet = async () => {
    if (!form.name.trim()) { toast.error('Merkez adı girilmeli'); return; }
    setKaydediyor(true);
    try {
      if (dialog?.id) await api.patch(`/sorumluluk-merkezleri/${dialog.id}`, form);
      else await api.post('/sorumluluk-merkezleri', form);
      toast.success(dialog?.id ? 'Merkez güncellendi' : 'Merkez eklendi');
      setDialog(null); yukle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setKaydediyor(false); }
  };

  const pasifeAl = async (m) => {
    if (!window.confirm(`"${m.name}" pasife alınsın mı? Geçmiş kayıtlar merkeze bağlı kalır.`)) return;
    try { await api.delete(`/sorumluluk-merkezleri/${m.id}`); toast.success('Merkez pasife alındı'); yukle(); }
    catch { toast.error('İşlem başarısız'); }
  };

  const turEtiket = { profit_center: 'Kâr Merkezi', cost_center: 'Gider Merkezi' };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <Target size={18} /> Sorumluluk Merkezleri
          </CardTitle>
          <p className="text-xs text-[#8a7e70] mt-1">
            Şirket altındaki iş/proje kırılımı. Kârlılık her merkezin kendi para biriminde ölçülür.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#3F3E39] cursor-pointer">
            <Checkbox checked={hepsi} onCheckedChange={(v) => setHepsi(!!v)} />
            Pasifleri de göster
          </label>
          <Button size="sm" onClick={() => ac(null)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
            <Plus size={14} className="mr-1" /> Yeni Merkez
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kod</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ana Para Birimi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tür</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {merkezler.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#8a7e70] py-8">
                Henüz sorumluluk merkezi tanımlanmamış.
              </TableCell></TableRow>
            )}
            {merkezler.map(m => (
              <TableRow key={m.id} className="border-[#d0c4b0]">
                <TableCell className="font-mono font-semibold text-[#1a1916]">{m.kod || '—'}</TableCell>
                <TableCell className="text-sm font-medium text-[#1a1916]">{m.name}</TableCell>
                <TableCell><Badge className="bg-[#2e2d29] text-[#f5f0e8]">{m.para_birimi}</Badge></TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{turEtiket[m.type] || m.type}</TableCell>
                <TableCell>
                  {m.is_active
                    ? <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Aktif</span>
                    : <span className="status-badge bg-[#8a7e70]/10 text-[#8a7e70]">Pasif</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => ac(m)} title="Düzenle">
                    <Pencil size={14} />
                  </Button>
                  {m.is_active && silmeYetkisi && (
                    <Button size="sm" variant="ghost" onClick={() => pasifeAl(m)} title="Pasife al">
                      <Trash2 size={14} className="text-[#a34b3f]" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {dialog?.id ? 'Sorumluluk Merkezini Düzenle' : 'Yeni Sorumluluk Merkezi'}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8a7e70]">
              Kârlılık bu merkezin ana para biriminde raporlanır; TL harcamalar harcamanın
              gerçekleştiği günün TCMB döviz alış kuruyla çevrilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Merkez Adı</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                       placeholder="Hilltown Beach Club" className="bg-white border-[#d0c4b0]" />
              </div>
              <div className="space-y-2">
                <Label>Kod</Label>
                <Input value={form.kod || ''} onChange={(e) => setForm(p => ({ ...p, kod: e.target.value }))}
                       placeholder="HBC" className="bg-white border-[#d0c4b0]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ana Para Birimi</Label>
                <Select value={form.para_birimi} onValueChange={(v) => setForm(p => ({ ...p, para_birimi: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['TRY', 'GBP', 'EUR', 'USD'].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profit_center">Kâr Merkezi</SelectItem>
                    <SelectItem value="cost_center">Gider Merkezi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {kurOnizleme?.kur && (
              <div className="text-xs text-[#3F3E39] bg-[#e8dfd0] rounded px-3 py-2">
                1.000 TL = <strong>{kurOnizleme.tutar?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} {form.para_birimi}</strong>
                {' · '}kur {kurOnizleme.kur}{' · '}
                {kurOnizleme.kur_tarihi} tarihli TCMB bülteni (döviz alış)
              </div>
            )}
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Input value={form.aciklama || ''} onChange={(e) => setForm(p => ({ ...p, aciklama: e.target.value }))}
                     className="bg-white border-[#d0c4b0]" />
            </div>
            {dialog?.id && !dialog.is_active && (
              <label className="flex items-center gap-2 text-sm text-[#3F3E39] cursor-pointer">
                <Checkbox checked={!!form.is_active} onCheckedChange={(v) => setForm(p => ({ ...p, is_active: !!v }))} />
                Yeniden aktif et
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={kaydet} disabled={kaydediyor} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── DEPARTMAN VE POZİSYONLAR ───
//
// Bu tanımlar personel açarken satır içinde de yaratılabiliyor, ama yeni bir
// şirkette liste bomboş başladığı için satır içi yaratma tek başına yetmiyordu:
// kullanıcı neyi nereden ekleyeceğini göremiyordu. Burası listeyi baştan
// kurmanın, yeniden adlandırmanın ve kullanılmayanı pasife almanın yeri.
//
// Pozisyon bir departmana bağlanabilir (zorunlu değil): personel formunda
// departman seçilince pozisyon listesi ona göre süzülüyor.
// Personel görünürlük kapsamı — sunucu karşılığı routes/organizasyon.py KAPSAMLAR
const KAPSAM_SECENEKLERI = [
  { deger: 'yok', etiket: 'Diğer personeli göremez' },
  { deger: 'departman', etiket: 'Sorumlusu olduğu departmanın personelini görebilir' },
  { deger: 'tumu', etiket: 'Tüm personeli görebilir' },
];
const KAPSAM_ACIKLAMA = {
  yok: 'Personel listesi, maaş, puantaj ve izin ekranlarında yalnızca kendi kaydını görür.',
  departman: 'Departman ve Pozisyonlar ekranından sorumlusu olarak atandığı departmanların personelini görür ve puantajını girer. Kullanıcının bir personele bağlı olması gerekir.',
  tumu: 'Bütün personeli görür ve puantajını girebilir; tahakkuk için de bu kapsam gerekir (İK / muhasebe).',
};

function DepartmanTab() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('employees', 'delete');
  const [departmanlar, setDepartmanlar] = useState([]);
  const [pozisyonlar, setPozisyonlar] = useState([]);
  const [hepsi, setHepsi] = useState(false);
  const [yeniDepartman, setYeniDepartman] = useState('');
  const [yeniPozisyon, setYeniPozisyon] = useState('');
  const [yeniPozisyonDept, setYeniPozisyonDept] = useState('');
  const [duzenlenen, setDuzenlenen] = useState(null); // {tur, id, ad}
  const [mesgul, setMesgul] = useState(false);

  // Sorumlu adayları: bu şirketin personeli + bu şirkette personel kaydı
  // OLMAYAN kullanıcılar (ör. öbür şirketin personeli olan muhasebeci)
  const [adaylar, setAdaylar] = useState({ personeller: [], kullanicilar: [] });

  const yukle = useCallback(() => {
    api.get('/hr/departments', { params: { hepsi: true } })
      .then(r => setDepartmanlar(r.data || [])).catch(() => toast.error('Departmanlar okunamadı'));
    api.get('/hr/positions', { params: { hepsi: true } })
      .then(r => setPozisyonlar(r.data || [])).catch(() => {});
    api.get('/organizasyon/sorumlu-adaylari')
      .then(r => setAdaylar(r.data || { personeller: [], kullanicilar: [] })).catch(() => {});
  }, []);
  useEffect(() => { yukle(); }, [yukle]);

  const ekle = async (tur) => {
    const ad = (tur === 'departman' ? yeniDepartman : yeniPozisyon).trim();
    if (!ad) return;
    setMesgul(true);
    try {
      if (tur === 'departman') {
        await api.post('/hr/departments', { name: ad });
        setYeniDepartman('');
      } else {
        await api.post('/hr/positions', { name: ad, department_id: yeniPozisyonDept || null });
        setYeniPozisyon('');
      }
      toast.success(`"${ad}" eklendi`);
      yukle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eklenemedi');
    } finally { setMesgul(false); }
  };

  const adiKaydet = async () => {
    if (!duzenlenen?.ad?.trim()) return;
    setMesgul(true);
    try {
      const yol = duzenlenen.tur === 'departman' ? 'departments' : 'positions';
      await api.patch(`/hr/${yol}/${duzenlenen.id}`, { name: duzenlenen.ad.trim() });
      toast.success('Güncellendi');
      setDuzenlenen(null); yukle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    } finally { setMesgul(false); }
  };

  const durumDegistir = async (tur, kayit) => {
    const yol = tur === 'departman' ? 'departments' : 'positions';
    setMesgul(true);
    try {
      if (kayit.is_active) {
        await api.delete(`/hr/${yol}/${kayit.id}`);
        toast.success(`"${kayit.name}" pasife alındı`);
      } else {
        await api.patch(`/hr/${yol}/${kayit.id}`, { is_active: true });
        toast.success(`"${kayit.name}" yeniden aktif`);
      }
      yukle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    } finally { setMesgul(false); }
  };

  // Açılır listede değer "tur:id" olarak taşınıyor: aynı id iki türde de
  // bulunabilir (kullanıcı ve personel ayrı koleksiyonlar), tür olmadan seçim
  // belirsiz kalırdı.
  const sorumluSec = async (dept, deger) => {
    const [tur, id] = (deger || '').split(':');
    setMesgul(true);
    try {
      await api.patch(`/hr/departments/${dept.id}`,
        { sorumlu_id: id || null, sorumlu_tur: tur || null });
      const kisi = [...adaylar.personeller, ...adaylar.kullanicilar]
        .find(a => a.tur === tur && a.id === id);
      toast.success(kisi
        ? `${dept.name} puantajını artık ${kisi.ad} giriyor`
        : `${dept.name} sorumlusu kaldırıldı`);
      yukle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sorumlu atanamadı');
    } finally { setMesgul(false); }
  };

  const gorunen = (liste) => hepsi ? liste : liste.filter(x => x.is_active);
  const deptAdi = (id) => departmanlar.find(d => d.id === id)?.name;

  const satir = (tur, kayit, altBilgi, ekHucre = null) => (
    <TableRow key={kayit.id} className="border-[#d0c4b0]">
      <TableCell className="text-sm font-medium text-[#1a1916]">
        {duzenlenen?.tur === tur && duzenlenen?.id === kayit.id ? (
          <div className="flex items-center gap-2">
            <Input value={duzenlenen.ad} autoFocus
              onChange={e => setDuzenlenen(p => ({ ...p, ad: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') adiKaydet(); if (e.key === 'Escape') setDuzenlenen(null); }}
              className="h-8 bg-white border-[#d0c4b0] max-w-[220px]" />
            <Button size="sm" onClick={adiKaydet} disabled={mesgul}
              className="h-8 bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
              <Check size={13} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDuzenlenen(null)} className="h-8">
              <X size={13} />
            </Button>
          </div>
        ) : (
          <span className={kayit.is_active ? '' : 'text-[#8a7e70] line-through'}>{kayit.name}</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-[#8a7e70]">{altBilgi}</TableCell>
      {ekHucre}
      <TableCell>
        {kayit.is_active
          ? <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Aktif</span>
          : <span className="status-badge bg-[#8a7e70]/10 text-[#8a7e70]">Pasif</span>}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" title="Adını değiştir" disabled={mesgul}
          onClick={() => setDuzenlenen({ tur, id: kayit.id, ad: kayit.name })}>
          <Pencil size={14} />
        </Button>
        {(!kayit.is_active || silmeYetkisi) && (
        <Button size="sm" variant="ghost" disabled={mesgul}
          title={kayit.is_active ? 'Pasife al' : 'Yeniden aktif et'}
          onClick={() => durumDegistir(tur, kayit)}>
          {kayit.is_active
            ? <Trash2 size={14} className="text-[#a34b3f]" />
            : <RefreshCw size={14} className="text-[#3d6e4e]" />}
        </Button>
        )}
      </TableCell>
    </TableRow>
  );

  const baslik = (metin, ek = null) => (
    <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">{metin}</TableHead>
      {ek && <TableHead className="text-[#1a1916] font-bold text-xs uppercase">{ek}</TableHead>}
      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
      <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">İşlem</TableHead>
    </TableRow>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* ── DEPARTMANLAR ── */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
              <Users size={18} /> Departmanlar
            </CardTitle>
            <label className="flex items-center gap-2 text-xs text-[#3F3E39] cursor-pointer">
              <Checkbox checked={hepsi} onCheckedChange={(v) => setHepsi(!!v)} />
              Pasifleri göster
            </label>
          </div>
          <p className="text-xs text-[#8a7e70]">
            <b>Puantaj sorumlusu</b> o departmanın günlük puantajını girer ve kullanıcı
            yetkisi "sorumlusu olduğu departman" ise yalnızca o personeli görür.
            Sorumlu atanmazsa puantajı yalnızca genel puantaj yetkilisi girebilir.
            Sorumlu <b>personel</b> olabilir (girişsiz bağlantıdan doldurur) ya da
            bu şirkette personel kaydı olmayan bir <b>kullanıcı</b> (uygulamadan doldurur).
          </p>
          <div className="flex items-center gap-2 pt-3">
            <Input value={yeniDepartman} onChange={e => setYeniDepartman(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ekle('departman'); }}
              placeholder="Yeni departman adı (ör. Mutfak)"
              className="bg-white border-[#d0c4b0] h-9" data-testid="yeni-departman" />
            <Button size="sm" onClick={() => ekle('departman')} disabled={mesgul || !yeniDepartman.trim()}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-9 whitespace-nowrap">
              <Plus size={14} className="mr-1" /> Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>{baslik('Personel / Pozisyon', 'Puantaj Sorumlusu')}</TableHeader>
            <TableBody>
              {gorunen(departmanlar).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#8a7e70] py-8">
                  Henüz departman yok. Yukarıdaki kutuya yazıp <b>Ekle</b>'ye basın.
                </TableCell></TableRow>
              )}
              {gorunen(departmanlar).map(d => satir('departman', d,
                `${d.personel_sayisi ?? 0} personel · ${d.pozisyon_sayisi ?? 0} pozisyon`,
                <TableCell key="sorumlu" className="text-sm">
                  <select value={d.sorumlu_id ? `${d.sorumlu_tur || 'personel'}:${d.sorumlu_id}` : ''}
                    disabled={mesgul || !d.is_active}
                    onChange={e => sorumluSec(d, e.target.value)}
                    data-testid={`dept-sorumlu-${d.id}`}
                    className="h-8 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm max-w-[210px] w-full">
                    <option value="">— sorumlu yok —</option>
                    <optgroup label="Personel (bağlantıdan girer)">
                      {adaylar.personeller.map(p => (
                        <option key={`personel:${p.id}`} value={`personel:${p.id}`}>{p.ad}</option>
                      ))}
                    </optgroup>
                    {adaylar.kullanicilar.length > 0 && (
                      <optgroup label="Kullanıcı (uygulamadan girer)">
                        {adaylar.kullanicilar.map(k => (
                          <option key={`kullanici:${k.id}`} value={`kullanici:${k.id}`}>{k.ad}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </TableCell>))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── POZİSYONLAR ── */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <Shield size={18} /> Pozisyonlar
          </CardTitle>
          <p className="text-xs text-[#8a7e70]">
            Departmana bağlamak zorunlu değil; bağlanırsa personel formunda o departman
            seçilince listede öne çıkar.
          </p>
          <div className="flex items-center gap-2 pt-3">
            <Input value={yeniPozisyon} onChange={e => setYeniPozisyon(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ekle('pozisyon'); }}
              placeholder="Yeni pozisyon adı (ör. Aşçı)"
              className="bg-white border-[#d0c4b0] h-9" data-testid="yeni-pozisyon" />
            <select value={yeniPozisyonDept} onChange={e => setYeniPozisyonDept(e.target.value)}
              className="h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm min-w-[130px]">
              <option value="">Departmansız</option>
              {departmanlar.filter(d => d.is_active).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <Button size="sm" onClick={() => ekle('pozisyon')} disabled={mesgul || !yeniPozisyon.trim()}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-9 whitespace-nowrap">
              <Plus size={14} className="mr-1" /> Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>{baslik('Departman / Personel')}</TableHeader>
            <TableBody>
              {gorunen(pozisyonlar).length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-[#8a7e70] py-8">
                  Henüz pozisyon yok. Yukarıdaki kutuya yazıp <b>Ekle</b>'ye basın.
                </TableCell></TableRow>
              )}
              {gorunen(pozisyonlar).map(pz => satir('pozisyon', pz,
                `${deptAdi(pz.department_id) || 'Departmansız'} · ${pz.personel_sayisi ?? 0} personel`))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
